<template>
  <main ref="formRootRef" class="">
    <!-- Inscrições Fechadas -->
    <div v-if="currentStep === 'closed'" class="flex justify-center py-8 md:px-8">
      <div class="w-full text-center">
        <div class="max-w-md mx-auto">
          <svg class="w-16 h-16 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
          </svg>
          <h2 class="text-2xl font-bold text-gray-900 mb-2">Inscrições Encerradas</h2>
          <p class="text-gray-600">{{ rsvpSettings?.closing_message || 'As inscrições para este evento estão encerradas.' }}</p>
        </div>
      </div>
    </div>

    <!-- Inscrições Esgotadas (limite global atingido) -->
    <div v-if="currentStep === 'sold_out'" class="flex justify-center py-8 md:px-8">
      <div class="w-full text-center">
        <div class="max-w-md mx-auto">
          <svg class="w-16 h-16 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"/>
          </svg>
          <h2 class="text-2xl font-bold text-gray-900 mb-2">Inscrições Esgotadas</h2>
          <p class="text-gray-600">{{ rsvpSettings?.limit_registration_message || 'As vagas para este evento já se esgotaram.' }}</p>
        </div>
      </div>
    </div>

    <!-- Multi-Step Form Section -->
    <section class="flex h-full flex-col overflow-hidden relative" v-if="showForm">
      <loading
        :active.sync="loading"
        :color="loaderColor"
        loader="dots"
        :can-cancel="false"
        :is-full-page="false"
      />

      <div class="flex-1">

        <!-- ============================================================ -->
        <!-- TELA PRÉ-FORM: Controle de Inscrição (sem step bar) -->
        <!-- ============================================================ -->
        <template v-if="registrationControlConfig && !registrationControlValidated">
          <div class="mb-8">
            <div class="text-center py-4 border-b border-gray-300">
              <span class="text-gray-900 font-regular">INSCRIÇÃO</span>
            </div>
          </div>
          <div class="space-y-4 py-4 px-8">
            <div class="w-full">
              <div class="relative w-full">
                <div class="mb-4">Informe o seu <strong>{{ registrationControlConfig.identifier_label }}</strong> para prosseguir.</div>
                <input
                  type="text"
                  v-model="registrationControlIdentifier"
                  v-maska
                  :data-maska="registrationControlConfig.identifier_mask || undefined"
                  :placeholder="registrationControlConfig.identifier_label + ' *'"
                  class="bg-white border border-gray-300 text-gray-900 text-sm rounded-md block w-full p-4 focus:outline-none focus:ring-1 focus:ring-gray-200"
                  :class="{ 'border-red-500': registrationControlError }"
                  autocomplete="off"
                  @keydown.enter.prevent="handleRegistrationControlValidation"
                />
                <span v-if="registrationControlError" class="text-red-500 text-sm mt-1 block">{{ registrationControlError }}</span>
                <div v-if="registrationControlItemName" class="mt-2 p-3 bg-green-50 border border-green-200 rounded-md">
                  <span class="text-green-800 text-sm font-medium">{{ registrationControlItemName }}</span>
                </div>
              </div>
            </div>
            <div class="flex w-full mt-4">
              <button
                type="button"
                @click="handleRegistrationControlValidation"
                class="button-subs w-full text-white px-4 py-2 rounded-md hover:bg-blue-600"
                :disabled="loading || !registrationControlIdentifier"
              >
                VALIDAR
              </button>
            </div>
          </div>
        </template>

        <!-- ============================================================ -->
        <!-- FLUXO NORMAL (só aparece quando RC não existe OU já validou) -->
        <!-- ============================================================ -->
        <template v-else>

        <!-- Progress Bar -->
        <div class="mb-8">
          <!-- Step Isolado (Identificação Inicial) -->
          <div v-if="wizardStep === 0" class="text-center py-4 border-b border-gray-300">
            <span class="text-gray-900 font-regular">
              INSCRIÇÃO
            </span>
          </div>

          <!-- Header para Formulário Simples (sem steps) -->
          <div v-else-if="!isWizardMode && wizardStep > 0" class="text-center py-4 border-b border-gray-300">
            <span class="text-gray-900 font-regular">
              INSCRIÇÃO
            </span>
          </div>

          <!-- Barra dos steps do formulário -->
          <div id="step-nav" v-else-if="visibleSteps && visibleSteps.length > 0" class="flex items-center justify-between border-b border-gray-300 gap-2 md:gap-4 overflow-hidden">
            <!-- Todos os steps (janela deslizante: formulário + facial + revisão) -->
            <div
              v-for="step in displayedSteps"
              :key="`step-nav-${step._uniqueId || step.id}`"
              class="flex-1 text-center py-2 transition-all duration-300 px-2 min-w-0"
              :class="{
                'text-steps border-b border-steps font-medium': wizardStep === step._originalIndex + 1,
                'text-gray-500': wizardStep !== step._originalIndex + 1
              }"
            >
              <span class="truncate block" :title="step?.name">
                <!-- Desktop: sempre nome completo -->
                <span class="hidden md:inline">
                  {{ step._originalIndex + 1 }}. {{ step?.name || 'Etapa ' + (step._originalIndex + 1) }}
                </span>
                <!-- Mobile: nome completo no ativo, só número nos outros -->
                <span class="md:hidden">
                  {{ step._originalIndex + 1 }}<template v-if="wizardStep === step._originalIndex + 1">. {{ getAbbreviatedLabel(step) }}</template><template v-else>.</template>
                </span>
              </span>
            </div>
          </div>
        </div>

        <!-- Step 0: Identificação (Lista de Convidados) -->
        <div v-show="wizardStep === 0" class="space-y-6 py-8">
          <form @submit.prevent="handleIdentification" class="form md:px-8 mb-4" autocomplete="off">
            <div class="w-full">
              <div class="relative w-full">
                <div class="mb-4">{{ identificationText }}</div>

                <!-- Campo Email -->
                <input
                  v-if="identificationType === 'email'"
                  type="email"
                  v-model="identificationValue"
                  placeholder="E-mail *"
                  class="bg-white border border-gray-300 text-gray-900 text-sm rounded-md block w-full p-4 focus:outline-none focus:ring-1 focus:ring-gray-200"
                  :class="{ 'border-red-500': identificationSubmitted && !identificationValue }"
                />

                <!-- Campo CPF -->
                <input
                  v-else
                  type="text"
                  v-model="identificationValue"
                  v-maska data-maska="###.###.###-##"
                  placeholder="000.000.000-00"
                  class="bg-white border border-gray-300 text-gray-900 text-sm rounded-md block w-full p-4 focus:outline-none focus:ring-1 focus:ring-gray-200"
                  :class="{ 'border-red-500': identificationSubmitted && !identificationValue }"
                />

                <span v-if="identificationSubmitted && !identificationValue" class="text-red-500 text-sm">
                  {{ identificationType === 'cpf' ? 'CPF é obrigatório.' : 'E-mail é obrigatório.' }}
                </span>
                <span v-if="identificationError" class="text-red-500 text-sm">{{ identificationError }}</span>
              </div>
            </div>
            <div class="flex w-full mt-4">
              <button
                type="submit"
                class="button-subs w-full text-white px-4 py-2 rounded-md hover:bg-blue-600"
                :disabled="loading"
              >
                PROSSEGUIR
              </button>
            </div>
          </form>
        </div>

        <!-- Modo "somente declínio": inscrito que reabre vê apenas o card de declínio -->
        <div v-if="declineOnly && wizardStep > 0" class="form md:px-8 mb-4">
          <div class="mt-3">
            <button type="button" @click="handleDecline" :disabled="isSubmitting || loading" class="decline-card">
              <span class="decline-card-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
              </span>
              <span class="decline-card-text">
                <span class="decline-card-title">Não poderá participar?</span>
                <span class="decline-card-sub">Libere sua vaga para outra pessoa da lista de espera</span>
              </span>
              <svg class="decline-card-chevron" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 18l6-6-6-6"/></svg>
            </button>
          </div>
        </div>

        <!-- Formulário SEM steps (formulário simples) -->
        <form v-if="!isWizardMode && wizardStep > 0 && !isReviewStep && !declineOnly" @submit.prevent="handleSubmit" class="form md:px-8 mb-4" autocomplete="off">
          <div class="space-y-4">
            <!-- Mensagem de erro geral -->
            <div v-if="formError" class="bg-red-50 border border-red-200 rounded-md p-4 mb-4">
              <div class="flex items-start">
                <svg class="w-5 h-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"/>
                </svg>
                <div>
                  <p class="text-sm text-red-800 font-medium">Erro ao processar formulário</p>
                  <p class="text-sm text-red-700 mt-1">{{ formError }}</p>
                </div>
              </div>
            </div>

            <!-- Mensagem informativa: dados não podem ser alterados -->
            <div v-if="!canEditData" class="bg-yellow-50 border border-yellow-200 rounded-md p-4 mb-4">
              <div class="flex items-start">
                <svg class="w-5 h-5 text-yellow-600 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>
                </svg>
                <div>
                  <p class="text-sm text-yellow-800 font-medium">Seus dados estão bloqueados para edição</p>
                  <p class="text-sm text-yellow-700 mt-1">Sua inscrição já está confirmada ou o período de alterações foi encerrado. Entre em contato conosco caso precise realizar alguma alteração.</p>
                </div>
              </div>
            </div>

            <!-- Campos do formulário (sem steps) -->
            <div class="flex flex-wrap -mx-2">
              <template v-for="field in allVisibleFields" :key="field?.id || field?.name">
                <!-- Seção/Divider -->
                <div v-if="field?.type === 'section' || field?.component === 'SectionDivider'" class="w-full mb-2 mt-6 px-2">
                  <div class="text-md font-normal">{{ field?.label }}</div>
                  <hr class="mt-2" />
                </div>

                <!-- Grid de campos -->
                <div v-else-if="field" :class="getFieldContainerClass(field)">
                  <!-- Label (hidden for checkbox/terms — label is inline with checkbox) -->
                  <div v-if="!isCheckboxField(field) && !isTermsAcceptanceField(field) && !isFakeTicketField(field) && !isDisclaimerField(field) && !isInstrucoesField(field)" class="mb-2 text-sm">
                    {{ field?.label }}
                    <span v-if="field?.required" class="text-red-500">*</span>
                    <a
                      v-if="field?.label_link_text && field?.label_link_url"
                      :href="field.label_link_url"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="ml-2 text-xs text-blue-600 underline hover:text-blue-800"
                    >{{ field.label_link_text }}</a>
                  </div>

                  <!-- Input Phone -->
                  <input
                    v-if="isPhoneField(field)"
                    type="tel"
                    v-model="formData[field.name]"
                    :placeholder="field.placeholder || '(00) 0000-0000'"
                    :disabled="isFieldDisabled(field)"
                    :readonly="isFieldReadonly(field)"
                    v-maska:[phoneMaskOpts]
                    class="bg-white border border-gray-300 text-gray-900 text-sm rounded-md block w-full p-4 focus:outline-none focus:ring-1 focus:ring-gray-200"
                    :class="{
                      'border-red-500': hasFieldError(field),
                      'bg-gray-100': isFieldDisabled(field) || isFieldReadonly(field)
                    }"
                    @blur="touchField(field.name)"
                  />

                  <!-- Input CPF -->
                  <input
                    v-else-if="isCpfField(field)"
                    type="text"
                    v-model="formData[field.name]"
                    :placeholder="field.placeholder || '000.000.000-00'"
                    :disabled="isFieldDisabled(field)"
                    :readonly="isFieldReadonly(field)"
                    v-maska :data-maska="field.mask || '###.###.###-##'"
                    class="bg-white border border-gray-300 text-gray-900 text-sm rounded-md block w-full p-4 focus:outline-none focus:ring-1 focus:ring-gray-200"
                    :class="{
                      'border-red-500': hasFieldError(field),
                      'bg-gray-100': isFieldDisabled(field) || isFieldReadonly(field)
                    }"
                    @input="delete asyncErrors[field.name]"
                    @blur="touchField(field.name)"
                  />

                  <!-- Input CNPJ / CPF+CNPJ -->
                  <input
                    v-else-if="isCnpjField(field)"
                    type="text"
                    v-model="formData[field.name]"
                    :placeholder="field.placeholder || '00.000.000/0000-00'"
                    :disabled="isFieldDisabled(field)"
                    :readonly="isFieldReadonly(field)"
                    v-maska :data-maska="field.mask || '##.###.###/####-##'"
                    class="bg-white border border-gray-300 text-gray-900 text-sm rounded-md block w-full p-4 focus:outline-none focus:ring-1 focus:ring-gray-200"
                    :class="{
                      'border-red-500': hasFieldError(field),
                      'bg-gray-100': isFieldDisabled(field) || isFieldReadonly(field)
                    }"
                    @input="delete asyncErrors[field.name]"
                    @blur="touchField(field.name)"
                  />

                  <!-- Input CEP -->
                  <input
                    v-else-if="isCepField(field)"
                    type="text"
                    v-model="formData[field.name]"
                    :placeholder="field.placeholder || '00000-000'"
                    :disabled="isFieldDisabled(field)"
                    :readonly="isFieldReadonly(field)"
                    v-maska :data-maska="field.mask || '#####-###'"
                    class="bg-white border border-gray-300 text-gray-900 text-sm rounded-md block w-full p-4 focus:outline-none focus:ring-1 focus:ring-gray-200"
                    :class="{
                      'border-red-500': hasFieldError(field),
                      'bg-gray-100': isFieldDisabled(field) || isFieldReadonly(field)
                    }"
                    @input="onFieldInput(field)"
                    @blur="touchField(field.name)"
                  />

                  <!-- Terms Acceptance (scroll + checkbox) -->
                  <i-terms-acceptance
                    v-else-if="isTermsAcceptanceField(field)"
                    :modelValue="formData[field.name] === true"
                    @update:modelValue="val => { formData[field.name] = val; touchField(field.name); }"
                    :label="field.label || ''"
                    :config="field.config || {}"
                    :hasError="hasFieldError(field)"
                    :disabled="isFieldDisabled(field)"
                    :required="field.required"
                  />

                  <!-- Fake Ticket (display-only) -->
                  <i-fake-ticket
                    v-else-if="isFakeTicketField(field)"
                    :config="field.config || {}"
                  />

                  <!-- Disclaimer (display-only) -->
                  <i-disclaimer
                    v-else-if="isDisclaimerField(field)"
                    :config="field.config || {}"
                  />

                  <!-- Instruções (display-only ou com aceite) -->
                  <i-instrucoes
                    v-else-if="isInstrucoesField(field)"
                    :modelValue="formData[field.name] === true"
                    @update:modelValue="val => { formData[field.name] = val; touchField(field.name); }"
                    :config="field.config || {}"
                    :hasError="hasFieldError(field)"
                  />

                  <!-- Input Text -->
                  <input
                    v-else-if="isTextField(field)"
                    :type="getInputType(field)"
                    v-model="formData[field.name]"
                    :placeholder="field.placeholder"
                    :disabled="isFieldDisabled(field)"
                    :readonly="isFieldReadonly(field)"
                    :maxlength="getFieldMaxLength(field) || undefined"
                    v-maska :data-maska="field.mask"
                    class="bg-white border border-gray-300 text-gray-900 text-sm rounded-md block w-full p-4 focus:outline-none focus:ring-1 focus:ring-gray-200"
                    :class="{
                      'border-red-500': hasFieldError(field),
                      'bg-gray-100': isFieldDisabled(field) || isFieldReadonly(field)
                    }"
                    @input="delete asyncErrors[field.name]"
                    @blur="touchField(field.name)"
                  />

                  <!-- Textarea -->
                  <textarea
                    v-else-if="isTextareaField(field)"
                    v-model="formData[field.name]"
                    :placeholder="field.placeholder"
                    :disabled="isFieldDisabled(field)"
                    :readonly="isFieldReadonly(field)"
                    :maxlength="getFieldMaxLength(field) || undefined"
                    rows="3"
                    class="bg-white border border-gray-300 text-gray-900 text-sm rounded-md block w-full p-4 focus:outline-none focus:ring-1 focus:ring-gray-200"
                    :class="{
                      'border-red-500': hasFieldError(field),
                      'bg-gray-100': isFieldDisabled(field) || isFieldReadonly(field)
                    }"
                    @blur="touchField(field.name)"
                  />

                  <!-- Multiselect -->
                  <IMultiselect
                    v-else-if="isMultiselectField(field)"
                    v-model="formData[field.name]"
                    :options="getFieldOptions(field)"
                    :placeholder="field?.placeholder || 'Selecione'"
                    :disabled="isFieldDisabled(field)"
                    :filter="field.config?.searchable !== false"
                    :error="hasFieldError(field)"
                    @change="touchField(field.name)"
                  />

                  <!-- Select -->
                  <ISelect
                    v-else-if="isSelectField(field)"
                    :modelValue="formData[field.name]"
                    @update:modelValue="val => { formData[field.name] = val; touchField(field.name) }"
                    :options="getFieldOptions(field)"
                    :placeholder="field?.placeholder || 'Selecione'"
                    :disabled="isFieldDisabled(field)"
                    :error="hasFieldError(field)"
                  />

                  <!-- Radio -->
                  <div v-else-if="isRadioField(field)" class="flex gap-4 flex-wrap">
                    <label
                      v-for="opt in getFieldOptions(field)"
                      :key="opt?.value || opt"
                      class="flex items-center cursor-pointer"
                    >
                      <input
                        type="radio"
                        :name="field.name"
                        :value="opt?.value"
                        v-model="formData[field.name]"
                        class="mr-2 custom-radio"
                        :class="{ 'border-red-500': hasFieldError(field) }"
                        @change="touchField(field.name)"
                      />
                      <span class="text-sm">{{ opt?.label }}</span>
                    </label>
                  </div>

                  <!-- Checkbox Single / Terms -->
                  <div v-else-if="isCheckboxField(field)" class="relative">
                    <label class="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        v-model="formData[field.name]"
                        class="mr-2 custom-checkbox"
                        :class="{ 'border-red-500': hasFieldError(field) }"
                        @change="touchField(field.name)"
                      />
                      <span class="text-sm" v-html="renderCheckboxLabel(field)"></span>
                    </label>
                  </div>

                  <!-- Date -->
                  <input
                    v-else-if="isDateField(field)"
                    type="text"
                    inputmode="numeric"
                    v-maska
                    data-maska="##/##/####"
                    placeholder="DD/MM/AAAA"
                    v-model="formData[field.name]"
                    :disabled="isFieldDisabled(field)"
                    :readonly="isFieldReadonly(field)"
                    class="bg-white border border-gray-300 text-gray-900 rounded-md block w-full p-4 focus:outline-none focus:ring-1 focus:ring-gray-200"
                    :class="{
                      'border-red-500': hasFieldError(field),
                      'bg-gray-100': isFieldDisabled(field) || isFieldReadonly(field)
                    }"
                    @blur="touchField(field.name)"
                  />

                  <!-- Date + Time -->
                  <input
                    v-else-if="isDatetimeField(field)"
                    type="text"
                    inputmode="numeric"
                    v-maska
                    data-maska="##/##/#### ##:##"
                    placeholder="DD/MM/AAAA HH:MM"
                    v-model="formData[field.name]"
                    :disabled="isFieldDisabled(field)"
                    :readonly="isFieldReadonly(field)"
                    class="bg-white border border-gray-300 text-gray-900 text-sm rounded-md block w-full p-4 focus:outline-none focus:ring-1 focus:ring-gray-200"
                    :class="{
                      'border-red-500': hasFieldError(field),
                      'bg-gray-100': isFieldDisabled(field) || isFieldReadonly(field)
                    }"
                    @blur="touchField(field.name)"
                  />

                  <!-- File Upload -->
                  <div v-else-if="isFileField(field)" class="relative">
                    <input
                      type="file"
                      :ref="el => setFileRef(field.name, el)"
                      :accept="field.config?.accept"
                      class="hidden"
                      @change="onFileChange(field.name, $event)"
                    />
                    <button
                      type="button"
                      @click="triggerFileInput(field.name)"
                      class="bg-white border border-gray-300 text-gray-900 text-sm rounded-md w-full p-4 text-left focus:outline-none focus:ring-1 focus:ring-gray-200 flex items-center justify-between"
                      :class="{ 'border-red-500': hasFieldError(field) }"
                    >
                      <span v-if="files[field.name]" class="truncate">{{ files[field.name].name }}</span>
                      <span v-else class="text-gray-400">{{ field.placeholder || 'Escolher arquivo' }}</span>
                      <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                    </button>
                    <button
                      v-if="files[field.name]"
                      type="button"
                      @click="clearFile(field.name)"
                      class="absolute right-2 top-1/2 -translate-y-1/2 text-red-500 hover:text-red-700"
                    >
                      <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>

                  <!-- Error Message -->
                  <p v-if="hasFieldError(field)" class="text-red-500 text-xs mt-1">
                    {{ getFieldErrorMessage(field) }}
                  </p>

                  <!-- Help Text (esconde quando tem erro) -->
                  <p v-if="field.help_text && !hasFieldError(field)" class="text-gray-500 text-xs mt-1">
                    {{ field.help_text }}
                  </p>
                </div>
              </template>
            </div>
          </div>

          <!-- Botão de envio para formulário sem steps -->
          <div class="flex justify-end gap-4 mt-6">
            <button
              type="submit"
              :disabled="isSubmitting || loading || !canEditData"
              class="button-subs w-full text-white px-4 py-2 rounded-md hover:bg-blue-600"
            >
              {{ isSubmitting ? 'Enviando...' : (customization?.form_button_label || 'Enviar Inscrição') }}
            </button>
          </div>
          <!-- Card de declínio (abaixo do Enviar) -->
          <div v-if="canDecline" class="mt-3">
            <button type="button" @click="handleDecline" :disabled="isSubmitting || loading" class="decline-card">
              <span class="decline-card-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
              </span>
              <span class="decline-card-text">
                <span class="decline-card-title">Não poderá participar?</span>
                <span class="decline-card-sub">Libere sua vaga para outra pessoa da lista de espera</span>
              </span>
              <svg class="decline-card-chevron" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 18l6-6-6-6"/></svg>
            </button>
          </div>
        </form>

        <!-- Formulário COM steps (modo wizard) -->
        <form v-if="isWizardMode && wizardStep > 0 && !isReviewStep && !isTicketStep && !isCheckoutStep && !declineOnly" @submit.prevent="handleSubmit" class="form md:px-8 mb-4" autocomplete="off">

          <!-- Steps de campos do formulário -->
          <div
            v-for="(step, stepIndex) in visibleSteps"
            :key="step?.id || stepIndex"
            v-show="wizardStep === stepIndex + 1"
            class="space-y-4"
          >
            <!-- Mensagem de erro geral (visível no step atual) -->
            <div v-if="formError && wizardStep === stepIndex + 1" class="bg-red-50 border border-red-200 rounded-md p-4 mb-4">
              <div class="flex items-start">
                <svg class="w-5 h-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"/>
                </svg>
                <div>
                  <p class="text-sm text-red-800 font-medium">Erro ao processar formulário</p>
                  <p class="text-sm text-red-700 mt-1">{{ formError }}</p>
                </div>
              </div>
            </div>

            <!-- Mensagem informativa: dados não podem ser alterados -->
            <div v-if="!canEditData && stepIndex === 0" class="bg-yellow-50 border border-yellow-200 rounded-md p-4 mb-4">
              <div class="flex items-start">
                <svg class="w-5 h-5 text-yellow-600 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>
                </svg>
                <div>
                  <p class="text-sm text-yellow-800 font-medium">Seus dados estão bloqueados para edição</p>
                  <p class="text-sm text-yellow-700 mt-1">Sua inscrição já está confirmada ou o período de alterações foi encerrado. Entre em contato conosco caso precise realizar alguma alteração.</p>
                </div>
              </div>
            </div>

            <!-- Campos do Step -->
            <div class="flex flex-wrap -mx-2">
              <template v-for="field in getVisibleFields(step)" :key="field?.id || field?.name">
                <!-- Seção/Divider -->
                <div v-if="field?.type === 'section' || field?.component === 'SectionDivider'" class="w-full mb-2 mt-6 px-2">
                  <div class="text-md font-normal">{{ field?.label }}</div>
                  <hr class="mt-2" />
                </div>

                <!-- Grid de campos -->
                <div v-else-if="field" :class="getFieldContainerClass(field)">
                  <!-- Label (hidden for checkbox/terms — label is inline with checkbox) -->
                  <div v-if="!isCheckboxField(field) && !isTermsAcceptanceField(field) && !isFakeTicketField(field) && !isDisclaimerField(field) && !isInstrucoesField(field)" class="mb-2 text-sm">
                    {{ field?.label }}
                    <span v-if="field?.required" class="text-red-500">*</span>
                    <a
                      v-if="field?.label_link_text && field?.label_link_url"
                      :href="field.label_link_url"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="ml-2 text-xs text-blue-600 underline hover:text-blue-800"
                    >{{ field.label_link_text }}</a>
                  </div>

                  <!-- Input Phone -->
                  <input
                    v-if="isPhoneField(field)"
                    type="tel"
                    v-model="formData[field.name]"
                    :placeholder="field.placeholder || '(00) 0000-0000'"
                    :disabled="isFieldDisabled(field)"
                    :readonly="isFieldReadonly(field)"
                    v-maska:[phoneMaskOpts]
                    class="bg-white border border-gray-300 text-gray-900 text-sm rounded-md block w-full p-4 focus:outline-none focus:ring-1 focus:ring-gray-200"
                    :class="{
                      'border-red-500': hasFieldError(field),
                      'bg-gray-100': isFieldDisabled(field) || isFieldReadonly(field)
                    }"
                    @blur="touchField(field.name)"
                  />

                  <!-- Input CPF -->
                  <input
                    v-else-if="isCpfField(field)"
                    type="text"
                    v-model="formData[field.name]"
                    :placeholder="field.placeholder || '000.000.000-00'"
                    :disabled="isFieldDisabled(field)"
                    :readonly="isFieldReadonly(field)"
                    v-maska :data-maska="field.mask || '###.###.###-##'"
                    class="bg-white border border-gray-300 text-gray-900 text-sm rounded-md block w-full p-4 focus:outline-none focus:ring-1 focus:ring-gray-200"
                    :class="{
                      'border-red-500': hasFieldError(field),
                      'bg-gray-100': isFieldDisabled(field) || isFieldReadonly(field)
                    }"
                    @input="delete asyncErrors[field.name]"
                    @blur="touchField(field.name)"
                  />

                  <!-- Input CNPJ / CPF+CNPJ -->
                  <input
                    v-else-if="isCnpjField(field)"
                    type="text"
                    v-model="formData[field.name]"
                    :placeholder="field.placeholder || '00.000.000/0000-00'"
                    :disabled="isFieldDisabled(field)"
                    :readonly="isFieldReadonly(field)"
                    v-maska :data-maska="field.mask || '##.###.###/####-##'"
                    class="bg-white border border-gray-300 text-gray-900 text-sm rounded-md block w-full p-4 focus:outline-none focus:ring-1 focus:ring-gray-200"
                    :class="{
                      'border-red-500': hasFieldError(field),
                      'bg-gray-100': isFieldDisabled(field) || isFieldReadonly(field)
                    }"
                    @input="delete asyncErrors[field.name]"
                    @blur="touchField(field.name)"
                  />

                  <!-- Input CEP -->
                  <input
                    v-else-if="isCepField(field)"
                    type="text"
                    v-model="formData[field.name]"
                    :placeholder="field.placeholder || '00000-000'"
                    :disabled="isFieldDisabled(field)"
                    :readonly="isFieldReadonly(field)"
                    v-maska :data-maska="field.mask || '#####-###'"
                    class="bg-white border border-gray-300 text-gray-900 text-sm rounded-md block w-full p-4 focus:outline-none focus:ring-1 focus:ring-gray-200"
                    :class="{
                      'border-red-500': hasFieldError(field),
                      'bg-gray-100': isFieldDisabled(field) || isFieldReadonly(field)
                    }"
                    @input="onFieldInput(field)"
                    @blur="touchField(field.name)"
                  />

                  <!-- Terms Acceptance (scroll + checkbox) -->
                  <i-terms-acceptance
                    v-else-if="isTermsAcceptanceField(field)"
                    :modelValue="formData[field.name] === true"
                    @update:modelValue="val => { formData[field.name] = val; touchField(field.name); }"
                    :label="field.label || ''"
                    :config="field.config || {}"
                    :hasError="hasFieldError(field)"
                    :disabled="isFieldDisabled(field)"
                    :required="field.required"
                  />

                  <!-- Fake Ticket (display-only) -->
                  <i-fake-ticket
                    v-else-if="isFakeTicketField(field)"
                    :config="field.config || {}"
                  />

                  <!-- Disclaimer (display-only) -->
                  <i-disclaimer
                    v-else-if="isDisclaimerField(field)"
                    :config="field.config || {}"
                  />

                  <!-- Instruções (display-only ou com aceite) -->
                  <i-instrucoes
                    v-else-if="isInstrucoesField(field)"
                    :modelValue="formData[field.name] === true"
                    @update:modelValue="val => { formData[field.name] = val; touchField(field.name); }"
                    :config="field.config || {}"
                    :hasError="hasFieldError(field)"
                  />

                  <!-- Input Text -->
                  <input
                    v-else-if="isTextField(field)"
                    :type="getInputType(field)"
                    v-model="formData[field.name]"
                    :placeholder="field.placeholder"
                    :disabled="isFieldDisabled(field)"
                    :readonly="isFieldReadonly(field)"
                    :maxlength="getFieldMaxLength(field) || undefined"
                    v-maska :data-maska="field.mask"
                    class="bg-white border border-gray-300 text-gray-900 text-sm rounded-md block w-full p-4 focus:outline-none focus:ring-1 focus:ring-gray-200"
                    :class="{
                      'border-red-500': hasFieldError(field),
                      'bg-gray-100': isFieldDisabled(field) || isFieldReadonly(field)
                    }"
                    @input="delete asyncErrors[field.name]"
                    @blur="touchField(field.name)"
                  />

                  <!-- Textarea -->
                  <textarea
                    v-else-if="isTextareaField(field)"
                    v-model="formData[field.name]"
                    :placeholder="field.placeholder"
                    :disabled="isFieldDisabled(field)"
                    :readonly="isFieldReadonly(field)"
                    :maxlength="getFieldMaxLength(field) || undefined"
                    rows="3"
                    class="bg-white border border-gray-300 text-gray-900 text-sm rounded-md block w-full p-4 focus:outline-none focus:ring-1 focus:ring-gray-200"
                    :class="{
                      'border-red-500': hasFieldError(field),
                      'bg-gray-100': isFieldDisabled(field) || isFieldReadonly(field)
                    }"
                    @blur="touchField(field.name)"
                  />

                  <!-- Multiselect -->
                  <IMultiselect
                    v-else-if="isMultiselectField(field)"
                    v-model="formData[field.name]"
                    :options="getFieldOptions(field)"
                    :placeholder="field?.placeholder || 'Selecione'"
                    :disabled="isFieldDisabled(field)"
                    :filter="field.config?.searchable !== false"
                    :error="hasFieldError(field)"
                    @change="touchField(field.name)"
                  />

                  <!-- Select -->
                  <ISelect
                    v-else-if="isSelectField(field)"
                    :modelValue="formData[field.name]"
                    @update:modelValue="val => { formData[field.name] = val; touchField(field.name) }"
                    :options="getFieldOptions(field)"
                    :placeholder="field?.placeholder || 'Selecione'"
                    :disabled="isFieldDisabled(field)"
                    :error="hasFieldError(field)"
                  />

                  <!-- Radio -->
                  <div v-else-if="isRadioField(field)" class="flex gap-4 flex-wrap">
                    <label
                      v-for="opt in getFieldOptions(field)"
                      :key="opt?.value || opt"
                      class="flex items-center cursor-pointer"
                    >
                      <input
                        type="radio"
                        :name="field.name"
                        :value="opt?.value"
                        v-model="formData[field.name]"
                        class="mr-2 custom-radio"
                        :class="{ 'border-red-500': hasFieldError(field) }"
                        @change="touchField(field.name)"
                      />
                      <span class="text-sm">{{ opt?.label }}</span>
                    </label>
                  </div>

                  <!-- Checkbox Single / Terms -->
                  <div v-else-if="isCheckboxField(field)" class="relative">
                    <label class="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        v-model="formData[field.name]"
                        :id="field.name"
                        class="mr-2 custom-radio"
                        :class="{ 'border-red-500': hasFieldError(field) }"
                        @change="touchField(field.name)"
                      />
                      <span class="text-sm" v-html="renderCheckboxLabel(field)"></span>
                    </label>
                  </div>

                  <!-- File Upload -->
                  <div v-else-if="isFileField(field)" class="upload-box" @click="triggerFileInput(field.name)">
                    <input
                      type="file"
                      :ref="el => setFileRef(field.name, el)"
                      @change="onFileChange(field.name, $event)"
                      class="hidden"
                      :accept="field.config?.accept || '*'"
                    />
                    <div v-if="!files[field.name]" class="text-center">
                      <div class="flex justify-center mb-2">
                        <svg class="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"/>
                        </svg>
                      </div>
                      <p class="text-gray-600">
                        Arraste e solte ou <span class="text-blue-500">clique para enviar</span>
                      </p>
                      <p class="text-sm text-gray-500 mt-1">{{ field.help_text || 'Anexe o arquivo solicitado' }}</p>
                    </div>
                    <div v-else class="text-center">
                      <p class="text-green-600">
                        Arquivo selecionado: {{ files[field.name].name }}
                      </p>
                      <span class="text-blue-500 cursor-pointer text-sm" @click.stop="clearFile(field.name)">Alterar</span>
                    </div>
                  </div>

                  <!-- Date Input -->
                  <input
                    v-else-if="isDateField(field)"
                    type="text"
                    inputmode="numeric"
                    v-maska
                    data-maska="##/##/####"
                    placeholder="DD/MM/AAAA"
                    v-model="formData[field.name]"
                    :disabled="isFieldDisabled(field)"
                    :readonly="isFieldReadonly(field)"
                    class="bg-white border border-gray-300 text-gray-900 rounded-md block w-full p-4 focus:outline-none focus:ring-1 focus:ring-gray-200"
                    :class="{
                      'border-red-500': hasFieldError(field),
                      'bg-gray-100': isFieldDisabled(field) || isFieldReadonly(field)
                    }"
                    @blur="touchField(field.name)"
                  />

                  <!-- Date + Time -->
                  <input
                    v-else-if="isDatetimeField(field)"
                    type="text"
                    inputmode="numeric"
                    v-maska
                    data-maska="##/##/#### ##:##"
                    placeholder="DD/MM/AAAA HH:MM"
                    v-model="formData[field.name]"
                    :disabled="isFieldDisabled(field)"
                    :readonly="isFieldReadonly(field)"
                    class="bg-white border border-gray-300 text-gray-900 text-sm rounded-md block w-full p-4 focus:outline-none focus:ring-1 focus:ring-gray-200"
                    :class="{
                      'border-red-500': hasFieldError(field),
                      'bg-gray-100': isFieldDisabled(field) || isFieldReadonly(field)
                    }"
                    @blur="touchField(field.name)"
                  />

                  <!-- Error Message -->
                  <span v-if="hasFieldError(field)" class="text-red-500 text-xs mt-1 block">
                    {{ getFieldErrorMessage(field) }}
                  </span>

                  <!-- Help Text (se não for file e não tiver erro) -->
                  <p v-if="field.help_text && !isFileField(field) && !hasFieldError(field)" class="text-xs text-gray-500 mt-1">{{ field.help_text }}</p>
                </div>
              </template>
            </div>
          </div>

          <!-- Step de Reconhecimento Facial -->
          <div v-show="isFacialStep" class="space-y-4">
            <FacialRecognition
              :submitted="formSubmitted"
              @capture="handleFacialCapture"
            />
          </div>

          <!-- Navigation Buttons -->
          <div class="flex flex-col-reverse md:flex-row justify-between mt-8 gap-4">
            <button
              v-if="canGoBack"
              type="button"
              @click="prevStep"
              class="bg-gray-500 w-full text-white px-4 py-2 rounded-md hover:bg-gray-600 disabled:opacity-50"
              :disabled="loading"
            >
              Anterior
            </button>
            <button
              type="button"
              @click="nextStep"
              :disabled="isSubmitting || loading || (isFacialStep && !facialBlob)"
              class="button-subs w-full text-white px-4 py-2 rounded-md hover:bg-blue-600"
            >
              {{ isFacialStep ? (reviewEnabled ? 'Revisar' : 'Enviar') : (isLastFormStep ? (facialEnabled ? 'Próximo' : (reviewEnabled ? 'Revisar' : 'Enviar')) : 'Próximo') }}
            </button>
          </div>
          <!-- Card de declínio (abaixo da navegação) -->
          <div v-if="canDecline" class="mt-3">
            <button type="button" @click="handleDecline" :disabled="isSubmitting || loading" class="decline-card">
              <span class="decline-card-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
              </span>
              <span class="decline-card-text">
                <span class="decline-card-title">Não poderá participar?</span>
                <span class="decline-card-sub">Libere sua vaga para outra pessoa da lista de espera</span>
              </span>
              <svg class="decline-card-chevron" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 18l6-6-6-6"/></svg>
            </button>
          </div>
        </form>

        <!-- Step de Seleção de Ingresso -->
        <div v-if="isTicketStep" class="space-y-4 px-8">
          <TicketSelectionStep
            v-if="participant"
            :eventSlug="eventSlug || 'evento'"
            :participantId="participant.id"
            @ticket-selected="handleTicketSelected"
            @continue="handleTicketContinue"
          />
          <div v-else class="bg-yellow-50 border border-yellow-200 rounded-md p-4">
            <p class="text-sm text-yellow-800">
              🔍 Debug: Aguardando dados do participante...
            </p>
          </div>
        </div>

        <!-- Step de Checkout / Pagamento -->
        <div v-if="isCheckoutStep" class="space-y-4">
          <CheckoutStepNew
            v-if="selectedTicket && participant"
            :ticketData="selectedTicket"
            :quantity="ticketQuantity"
            :total="ticketTotal"
            :participantId="participant.id"
            :paymentConfig="paymentConfig"
            :customerData="{
              name: participant.name,
              email: participant.email,
              phone: participant.phone,
              cpf: participant.cpf
            }"
            @pix-generated="handlePixGenerated"
            @boleto-generated="handleBoletoGenerated"
            @payment-complete="handlePaymentComplete"
            @back="goBackToTicketSelection"
          />
        </div>

        <!-- Step de Revisão -->
        <div v-if="isReviewStep" class="form md:px-8 mb-4">
          <div class="space-y-6">
            <!-- Mensagem de erro geral -->
            <div v-if="formError" class="bg-red-50 border border-red-200 rounded-md p-4 mb-4">
              <div class="flex items-start">
                <svg class="w-5 h-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"/>
                </svg>
                <div>
                  <p class="text-sm text-red-800 font-medium">Erro ao processar formulário</p>
                  <p class="text-sm text-red-700 mt-1">{{ formError }}</p>
                </div>
              </div>
            </div>

            <!-- Dados agrupados por step -->
            <div v-for="(step, stepIndex) in visibleSteps" :key="'review-' + (step?.id || stepIndex)" class="mb-6">
              <div class="border-b border-gray-200 pb-2 mb-4">
                <h3 class="text-sm font-medium text-gray-700">{{ step?.name || 'Etapa ' + (stepIndex + 1) }}</h3>
              </div>

              <div class="space-y-3">
                <template v-for="field in getVisibleFields(step)" :key="'review-field-' + field?.name">
                  <!-- Campo normal -->
                  <div v-if="field && !isFileField(field) && !isFakeTicketField(field) && !isDisclaimerField(field) && !isInstrucoesField(field) && field.type !== 'section' && (Array.isArray(formData[field.name]) ? formData[field.name].length > 0 : formData[field.name])" class="flex justify-between py-2 border-b border-gray-100">
                    <span class="text-xs text-gray-500">{{ field.label }}</span>
                    <span class="text-sm text-gray-900 font-medium">{{ getFieldDisplayValue(field) }}</span>
                  </div>

                  <!-- Arquivos -->
                  <div v-else-if="field && isFileField(field) && files[field.name]" class="flex justify-between items-center py-2 border-b border-gray-100">
                    <span class="text-xs text-gray-500">{{ field.label }}</span>
                    <span class="text-sm text-gray-900 font-medium flex items-center">
                      <svg class="w-3 h-3 mr-1 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                      </svg>
                      {{ files[field.name].name }}
                    </span>
                  </div>
                </template>
              </div>
            </div>

            <!-- Botões de Navegação -->
            <div class="flex flex-col-reverse md:flex-row justify-between mt-8 gap-4">
              <button
                type="button"
                @click="prevStep"
                class="bg-gray-500 w-full text-white px-4 py-2 rounded-md hover:bg-gray-600 disabled:opacity-50"
                :disabled="loading"
              >
                Anterior
              </button>
              <button
                type="button"
                @click="hasPayment ? registerParticipant() : handleSubmit()"
                :disabled="isSubmitting || loading || !canEditData"
                class="button-subs w-full text-white px-4 py-2 rounded-md hover:bg-blue-600"
              >
                {{ hasPayment ? 'Prosseguir para Ingresso' : (customization?.form_button_label || 'Enviar Inscrição') }}
              </button>
            </div>
            <!-- Card de declínio (abaixo do Enviar) -->
            <div v-if="canDecline" class="mt-3">
              <button type="button" @click="handleDecline" :disabled="isSubmitting || loading" class="decline-card">
                <span class="decline-card-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
                </span>
                <span class="decline-card-text">
                  <span class="decline-card-title">Não poderá participar?</span>
                  <span class="decline-card-sub">Libere sua vaga para outra pessoa da lista de espera</span>
                </span>
                <svg class="decline-card-chevron" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 18l6-6-6-6"/></svg>
              </button>
            </div>
          </div>
        </div>
        </template>
        <!-- Fim do fluxo normal (v-else do registration control) -->

      </div>
    </section>

    <!-- Thanks Section - Dynamic Thank You Page -->
    <DynamicThankYouPage
      v-if="showThanks"
      :thankYouPage="thankYouPageData"
      :participant="participant"
      :confirmationCode="confirmationCode"
    />

    <!-- Modal de confirmação de declínio -->
    <div v-if="showDeclineModal" class="decline-modal-overlay">
      <div class="decline-modal-backdrop" @click="showDeclineModal = false"></div>
      <div class="decline-modal">
        <div class="decline-modal-body">
          <div class="decline-modal-head">
            <span class="decline-modal-badge">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
            </span>
            <h3 class="decline-modal-title">Confirmação de Declínio</h3>
          </div>
          <p class="decline-modal-desc">Atenção: Esta ação não poderá ser desfeita. Ao continuar, sua inscrição será cancelada e sua vaga será liberada para outra pessoa da lista de espera.</p>
          <label class="decline-modal-term">
            <input type="checkbox" v-model="declineAccepted" />
            <span>Estou ciente e desejo cancelar minha inscrição.</span>
          </label>
        </div>
        <div class="decline-modal-foot">
          <button type="button" class="decline-btn-cancel" :disabled="isSubmitting" @click="showDeclineModal = false">Cancelar</button>
          <button type="button" class="decline-btn-confirm" :disabled="!declineAccepted || isSubmitting" @click="confirmDecline">
            {{ isSubmitting ? 'Processando...' : 'CONFIRMAR DECLÍNIO' }}
          </button>
        </div>
      </div>
    </div>
  </main>
</template>

<script>
import { ref, reactive, computed, onMounted, onBeforeUnmount, watch, nextTick } from 'vue';
import { vMaska } from 'maska';
import useVuelidate from '@vuelidate/core';
import { required, email, minLength, maxLength, sameAs, helpers } from '@vuelidate/validators';
import { useLandingPage } from '@/composables/useLandingPage';
import { shouldShowField, shouldShowStep } from '@/utils/conditionEvaluator';
import { executeBehaviors } from '@/utils/behaviorEngine';
import { getFieldMaxLength } from '@/utils/validationBuilder';
import {
  getGlobalFormSchema,
  identifyAndGetForm,
  checkParticipantByEmail,
  submitFormGlobal,
  validateFieldGlobal
} from '@/services/api/forms';
import api from '@/services/api';
import { getRegistrationControlConfig, validateRegistrationControl } from '@/services/api/registrationControl';
import Loading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/css/index.css';
import IMultiselect from '@/components/inputs/i-multiselect.vue';
import ISelect from '@/components/inputs/i-select.vue';
import ITermsAcceptance from '@/components/inputs/i-terms-acceptance.vue';
import IFakeTicket from '@/components/inputs/i-fake-ticket.vue';
import IDisclaimer from '@/components/inputs/i-disclaimer.vue';
import IInstrucoes from '@/components/inputs/i-instrucoes.vue';
import DynamicField from './DynamicField.vue';
import DynamicThankYouPage from './DynamicThankYouPage.vue';
import FacialRecognition from './FacialRecognition.vue';
import TicketSelectionStep from '../steps/TicketSelectionStep.vue';
import CheckoutStepNew from '../steps/CheckoutStepNew.vue';
import { getPaymentConfig } from '@/services/api/payment';

// Campo "Levará quantos acompanhantes?" gerado pelo form builder. É ele que
// dimensiona quantas abas de acompanhante ficam visíveis.
const COMPANION_COUNT_FIELD = 'companion_1_companion_count';

// Campos de acompanhante cujo label o builder numera ("Nome do acompanhante 2")
// quando o formulário permite mais de um acompanhante.
const COMPANION_NUMBERED_LABEL_FIELD = /^companion_\d+_companion_(name|email|phone)$/;

// Nome de aba gerado pelo builder ("Acompanhante 2"). Só nomes nesse padrão
// perdem o índice — uma aba renomeada à mão fica intacta.
const COMPANION_NUMBERED_STEP_NAME = /^Acompanhante \d+$/;
import { configurePagarme } from '@/services/api/pagarme';
import { initializeTracking, getSourceDataForApi, trackConversion, cleanupTracking } from '@/utils/sourceTracker';

// Lista de estados brasileiros
const brazilianStates = [
  { value: 'AC', label: 'Acre' },
  { value: 'AL', label: 'Alagoas' },
  { value: 'AP', label: 'Amapá' },
  { value: 'AM', label: 'Amazonas' },
  { value: 'BA', label: 'Bahia' },
  { value: 'CE', label: 'Ceará' },
  { value: 'DF', label: 'Distrito Federal' },
  { value: 'ES', label: 'Espírito Santo' },
  { value: 'GO', label: 'Goiás' },
  { value: 'MA', label: 'Maranhão' },
  { value: 'MT', label: 'Mato Grosso' },
  { value: 'MS', label: 'Mato Grosso do Sul' },
  { value: 'MG', label: 'Minas Gerais' },
  { value: 'PA', label: 'Pará' },
  { value: 'PB', label: 'Paraíba' },
  { value: 'PR', label: 'Paraná' },
  { value: 'PE', label: 'Pernambuco' },
  { value: 'PI', label: 'Piauí' },
  { value: 'RJ', label: 'Rio de Janeiro' },
  { value: 'RN', label: 'Rio Grande do Norte' },
  { value: 'RS', label: 'Rio Grande do Sul' },
  { value: 'RO', label: 'Rondônia' },
  { value: 'RR', label: 'Roraima' },
  { value: 'SC', label: 'Santa Catarina' },
  { value: 'SP', label: 'São Paulo' },
  { value: 'SE', label: 'Sergipe' },
  { value: 'TO', label: 'Tocantins' }
];

// Validação de CPF
function validateCPF(cpf) {
  if (!cpf) return true; // Se não tem valor, deixa o required cuidar
  cpf = cpf.replace(/[^\d]/g, '');
  if (cpf.length !== 11) return false;
  if (/^(\d)\1+$/.test(cpf)) return false;

  let sum = 0;
  for (let i = 0; i < 9; i++) {
    sum += parseInt(cpf.charAt(i)) * (10 - i);
  }
  let digit = 11 - (sum % 11);
  if (digit >= 10) digit = 0;
  if (digit !== parseInt(cpf.charAt(9))) return false;

  sum = 0;
  for (let i = 0; i < 10; i++) {
    sum += parseInt(cpf.charAt(i)) * (11 - i);
  }
  digit = 11 - (sum % 11);
  if (digit >= 10) digit = 0;
  if (digit !== parseInt(cpf.charAt(10))) return false;

  return true;
}

// Validação de CNPJ
function validateCNPJ(cnpj) {
  if (!cnpj) return true;
  cnpj = cnpj.replace(/[^\d]/g, '');
  if (cnpj.length !== 14) return false;
  if (/^(\d)\1+$/.test(cnpj)) return false;

  let tamanho = cnpj.length - 2;
  let numeros = cnpj.substring(0, tamanho);
  let digitos = cnpj.substring(tamanho);
  let soma = 0;
  let pos = tamanho - 7;
  for (let i = tamanho; i >= 1; i--) {
    soma += numeros.charAt(tamanho - i) * pos--;
    if (pos < 2) pos = 9;
  }
  let resultado = soma % 11 < 2 ? 0 : 11 - soma % 11;
  if (`${resultado}` !== digitos.charAt(0)) return false;

  tamanho = tamanho + 1;
  numeros = cnpj.substring(0, tamanho);
  soma = 0;
  pos = tamanho - 7;
  for (let i = tamanho; i >= 1; i--) {
    soma += numeros.charAt(tamanho - i) * pos--;
    if (pos < 2) pos = 9;
  }
  resultado = soma % 11 < 2 ? 0 : 11 - soma % 11;
  return `${resultado}` === digitos.charAt(1);
}

// Validação de CPF ou CNPJ (para campos cpf_cnpj)
function validateCpfCnpj(value) {
  if (!value) return true;
  const clean = value.replace(/[^\d]/g, '');
  if (clean.length <= 11) return validateCPF(value);
  return validateCNPJ(value);
}

// Validação de telefone
function validatePhone(phone) {
  if (!phone) return true;
  const cleaned = phone.replace(/[^\d]/g, '');
  return cleaned.length >= 10 && cleaned.length <= 11;
}

// Validação de CEP
function validateCEP(cep) {
  if (!cep) return true;
  const cleaned = cep.replace(/[^\d]/g, '');
  return cleaned.length === 8;
}

export default {
  name: 'RSVPForm',
  components: {
    Loading,
    DynamicField,
    DynamicThankYouPage,
    FacialRecognition,
    TicketSelectionStep,
    CheckoutStepNew,
    IMultiselect,
    ISelect,
    ITermsAcceptance,
    IFakeTicket,
    IDisclaimer,
    IInstrucoes
  },
  directives: {
    maska: vMaska
  },
  setup() {
    // State principal
    const loading = ref(false);
    const currentStep = ref('loading');
    const showForm = ref(false);
    const showThanks = ref(false);
    const formSchema = ref(null);
    const formData = reactive({});
    const phoneMaskOpts = { mask: ['(##) ####-####', '(##) #####-####'] };
    const fieldNameMap = {};  // mapa nome_unico -> nome_original (para campos com name duplicado)
    const files = ref({});
    const participant = ref(null);
    const groupId = ref(null);
    const formId = ref(null);
    const fileRefs = ref({});
    const thankYouPageData = ref(null);
    const confirmationCode = ref(null);
    const facialBlob = ref(null);

    // State de identificação
    const identificationValue = ref('');
    const identificationError = ref(null);
    const identificationSubmitted = ref(false);

    // Registration Control state
    const registrationControlConfig = ref(null);
    const registrationControlIdentifier = ref('');
    const registrationControlItemId = ref(null);
    const registrationControlError = ref(null);
    const registrationControlValidated = ref(false);
    const registrationControlItemName = ref('');
    const registrationControlFormId = ref(null);
    const registrationControlItemData = ref(null);
    const registrationControlRemainingSlots = ref(null); // null = ilimitado

    // State do formulário
    const wizardStep = ref(0);
    const minStep = ref(0); // Step mínimo (0 para lista importada, 1 para inscrições abertas)
    const formSubmitted = ref(false);
    const isSubmitting = ref(false);
    const touchedFields = ref({});
    const asyncErrors = reactive({}); // Erros assíncronos (ex: email duplicado)
    const asyncValidating = reactive({}); // Campos em validação assíncrona
    const formError = ref(null); // Erro geral do formulário

    // Permissões de edição
    const canEditData = ref(true); // Por padrão pode editar (novo participante)
    const allowDataChangeBeforeConfirmation = ref(false);
    // Auto-declínio: o inscrito pode declinar a participação pelo formulário
    const canDecline = ref(false);
    // Modo "somente declínio" na reedição: oculta os campos do form e exibe só o
    // card de declínio (parâmetro edit_decline_only no Settings RSVP).
    const declineOnly = ref(false);

    // Identificação por email (para inscrições abertas com pagamento)
    const identifiedEmail = ref(null);

    // Payment & Checkout State
    const hasPayment = ref(false);
    const eventSlug = ref(null);
    const selectedTicket = ref(null);
    const ticketQuantity = ref(1);
    const ticketTotal = ref(0);
    const paymentCompleted = ref(false);
    const paymentProcessing = ref(false);
    const pixData = ref(null);
    const paymentConfig = ref(null);
    const boletoData = ref(null);
    const showPaymentPending = ref(false);
    const registrationStatus = ref('incomplete');

    // Carrega settings via composable
    const { settings, customization } = useLandingPage();

    // Configurações RSVP
    const rsvpSettings = computed(() => settings.value?.rsvp);

    // Watch for settings to load payment configuration
    watch(settings, (newSettings) => {
      if (newSettings && newSettings.rsvp) {
        hasPayment.value = newSettings.rsvp.has_payment === true || newSettings.rsvp.has_payment === 1;

        // Tenta pegar o slug de várias formas
        const slugFromSettings = newSettings.slug || newSettings.event_slug;
        const slugFromRsvp = newSettings.rsvp.slug || newSettings.rsvp.event_slug;
        const slugFromUrl = window.location.pathname.split('/').filter(p => p)[0]; // Primeiro segmento não vazio

        eventSlug.value = slugFromSettings || slugFromRsvp || slugFromUrl || '';

      }
    }, { immediate: true });

    // Modo de registro: 1 = Lista Importada, 2 = Inscrições Abertas
    const isImportedList = computed(() => {
      return rsvpSettings.value?.registration_mode === 1;
    });

    // Tipo de identificação: 1 = Email, 2 = CPF
    const identificationType = computed(() => {
      if (isImportedList.value) {
        return rsvpSettings.value?.identification_type === 2 ? 'cpf' : 'email';
      }
      // Modos 2/3 com pagamento: sempre email
      return 'email';
    });

    // Texto de identificação
    const identificationText = computed(() => {
      if (isImportedList.value) {
        if (identificationType.value === 'email') {
          return 'Por favor, para prosseguir informe o seu endereço de e-mail.';
        }
        return 'Por favor, para prosseguir informe o seu CPF.';
      }
      // Modos 2/3 com pagamento
      return 'Por favor, informe o seu e-mail para iniciar a inscrição.';
    });

    // Propriedades do formulário
    const steps = computed(() => formSchema.value?.steps || []);
    const fields = computed(() => formSchema.value?.fields || []);

    // Todos os campos do schema (steps + diretos) - usado para avaliar condições
    // Campos de biblioteca em companion steps (ex: cargo) são prefixados para
    // que o condition evaluator use o nome correto no formData
    const allSchemaFields = computed(() => {
      const result = [];
      if (fields.value) result.push(...fields.value);
      steps.value.forEach(step => {
        if (step.fields) {
          if (step.is_companion_step) {
            const idx = step.companion_index || 1;
            const prefix = `companion_${idx}_`;
            step.fields.forEach(f => {
              // Campos já prefixados (companion_1_*) ficam como estão
              if (f.name.startsWith('companion_')) {
                result.push(f);
              } else {
                // Campos de biblioteca (cargo, cpf, etc.) recebem prefixo
                result.push({ ...f, name: `${prefix}${f.name}` });
              }
            });
          } else {
            result.push(...step.fields);
          }
        }
      });
      return result;
    });

    // Reconhecimento facial habilitado
    const facialEnabled = computed(() => formSchema.value?.enable_facial_recognition || false);

    // Configuração de acompanhantes
    const companionConfig = computed(() => {
      const config = formSchema.value?.companion_config;
      console.log('[companionConfig]', { config, formSchema: formSchema.value ? 'loaded' : 'null' });
      if (!config || !config.enabled) return null;
      return config;
    });

    // Calcula quantos companions são permitidos com base nas vagas restantes
    const effectiveMaxCompanions = computed(() => {
      const config = companionConfig.value;
      if (!config) return 0;
      const configMax = config.max_companions || 1;
      const remaining = registrationControlRemainingSlots.value;

      console.log('[effectiveMaxCompanions]', { configMax, remaining, config });

      // Se não há controle de vagas (null = ilimitado), usa o max configurado
      if (remaining === null || remaining === undefined) return configMax;

      // remaining inclui a vaga do principal, então companions = remaining - 1
      const availableForCompanions = Math.max(0, remaining - 1);
      return Math.min(configMax, availableForCompanions);
    });

    // Gera steps de acompanhantes dinamicamente
    // Se já existem companion steps no formbuilder (is_companion_step), não gera dinâmicos
    const companionSteps = computed(() => {
      if (!companionConfig.value) return [];

      // Se o formbuilder já tem companion steps, usa eles (prefixados em visibleSteps)
      const hasFormbuilderCompanionSteps = (steps.value || []).some(s => s.is_companion_step);
      if (hasFormbuilderCompanionSteps) return [];

      const config = companionConfig.value;
      const maxCompanions = effectiveMaxCompanions.value;
      const mode = config.mode || 'invitation';

      // Se não há vagas para acompanhantes, não gera steps
      if (maxCompanions <= 0) return [];
      const generatedSteps = [];

      for (let i = 1; i <= maxCompanions; i++) {
        const stepName = maxCompanions === 1 ? 'Acompanhante' : `Acompanhante ${i}`;

        // Campos padrão sempre presentes
        const fields = [
          {
            id: `companion_${i}_has_companion`,
            name: `companion_${i}_has_companion`,
            label: 'Levarei acompanhante?',
            type: 'select',
            required: true,
            options: [
              { label: 'Sim', value: 'Sim' },
              { label: 'Não', value: 'Não' }
            ],
            layout: { cols: '12' }
          }
        ];

        // Campos condicionais: aparecem se responder "Sim"
        const conditionalFields = [
          {
            id: `companion_${i}_companion_name`,
            name: `companion_${i}_companion_name`,
            label: 'Nome do acompanhante',
            type: 'text',
            required: true,
            placeholder: 'Nome completo',
            layout: { cols: '12' },
            conditions: {
              field: `companion_${i}_has_companion`,
              operator: 'equals',
              value: 'Sim'
            }
          },
          {
            id: `companion_${i}_companion_email`,
            name: `companion_${i}_companion_email`,
            label: 'E-mail do acompanhante',
            type: 'email',
            required: true,
            placeholder: 'email@exemplo.com',
            layout: { cols: '12' },
            conditions: {
              field: `companion_${i}_has_companion`,
              operator: 'equals',
              value: 'Sim'
            }
          },
          {
            id: `companion_${i}_companion_phone`,
            name: `companion_${i}_companion_phone`,
            label: 'Telefone do acompanhante',
            type: 'phone',
            required: false,
            placeholder: '(00) 00000-0000',
            mask: '(##) #####-####',
            layout: { cols: '12' },
            conditions: {
              field: `companion_${i}_has_companion`,
              operator: 'equals',
              value: 'Sim'
            }
          }
        ];

        fields.push(...conditionalFields);

        // No modo "direct", campos customizados podem ser adicionados
        // (futuramente, esses viriam do backend no companion_config)
        if (mode === 'direct' && config.custom_fields && config.custom_fields.length > 0) {
          config.custom_fields.forEach(customField => {
            const prefixedField = {
              ...customField,
              id: `companion_${i}_${customField.id}`,
              name: `companion_${i}_${customField.name}`,
              conditions: {
                field: `companion_${i}_has_companion`,
                operator: 'equals',
                value: 'Sim'
              }
            };
            fields.push(prefixedField);
          });
        }

        generatedSteps.push({
          id: `companion_step_${i}`,
          name: stepName,
          fields: fields,
          is_companion_step: true
        });
      }

      return generatedSteps;
    });

    // O builder numera os labels dos campos de acompanhante quando o formulário
    // permite mais de um ("Nome do acompanhante 2"). Com a pergunta de
    // quantidade ativa, o número só faz sentido se o titular declarar 2 ou mais:
    // declarando 1 — ou ainda sem responder — o label volta à forma singular.
    // Sem a pergunta de quantidade as abas são fixas, então respeita o schema.
    const companionLabelsNumbered = computed(() => {
      const hasCountField = (allSchemaFields.value || []).some(f => f.name === COMPANION_COUNT_FIELD);
      if (!hasCountField) return true;
      return Number(formData[COMPANION_COUNT_FIELD]) >= 2;
    });

    const stripCompanionLabelIndex = (label) => String(label ?? '').replace(/\s+\d+\s*$/, '');

    // Steps visíveis (baseado em condições) - DEVE vir antes de allVisibleFields
    const visibleSteps = computed(() => {
      try {
        // Se não tem steps definidos mas tem campos diretos, criar step sintético
        if (!steps.value || !steps.value.length) {
          // Se tem campos diretos E (tem pagamento OU está em modo wizard)
          if (fields.value && fields.value.length > 0 && (hasPayment.value || wizardStep.value > 0)) {
            // Cria um step sintético com todos os campos
            const syntheticStep = {
              id: 'synthetic_step_1',
              name: 'Dados Pessoais',
              fields: fields.value,
              isSynthetic: true
            };
            return [syntheticStep];
          }
          return [];
        }

        // Filtra steps visíveis baseado em condições
        const filtered = steps.value.filter(step => step && shouldShowStep(step, formData, allSchemaFields.value));

        // Filtra companion steps do formbuilder com base nas vagas restantes
        const maxAllowed = effectiveMaxCompanions.value;
        let companionStepCount = 0;
        const filteredBySlots = filtered.filter(step => {
          if (!step.is_companion_step) return true;
          companionStepCount++;
          return companionStepCount <= maxAllowed;
        });

        // Prefixa campos de biblioteca em companion steps para evitar colisão no formData
        // Campos já prefixados (companion_1_*) ficam como estão
        const numberedLabels = companionLabelsNumbered.value;
        const processed = filteredBySlots.map(step => {
          if (!step.is_companion_step) return step;
          const idx = step.companion_index || 1;
          const prefix = `companion_${idx}_`;

          // Levando um só acompanhante, a aba "Acompanhante 1" vira
          // "Acompanhante" — mesma regra dos labels dos campos.
          const stepName = (!numberedLabels && COMPANION_NUMBERED_STEP_NAME.test(step.name || ''))
            ? stripCompanionLabelIndex(step.name)
            : step.name;

          return {
            ...step,
            name: stepName,
            fields: (step.fields || []).map(field => {
              // "Nome do acompanhante 1" volta a "Nome do acompanhante" quando
              // o titular vai levar apenas um. Só os campos que o builder
              // numera são afetados — campo de biblioteca terminado em número
              // (ex.: "Telefone 2") fica intacto.
              const needsSingular = !numberedLabels && COMPANION_NUMBERED_LABEL_FIELD.test(field.name);
              const label = needsSingular ? stripCompanionLabelIndex(field.label) : field.label;

              // Campos já prefixados (companion_1_*): só o label pode mudar
              if (field.name.startsWith('companion_')) {
                return label === field.label ? field : { ...field, label };
              }
              return {
                ...field,
                label,
                name: `${prefix}${field.name}`,
                _originalName: field.name,
              };
            })
          };
        });

        // Adiciona companion steps dinâmicos após os steps regulares (se não houver formbuilder companion steps)
        if (companionSteps.value && companionSteps.value.length > 0) {
          return [...processed, ...companionSteps.value];
        }

        return processed;
      } catch (error) {
        console.error('Error in visibleSteps:', error);
        return [];
      }
    });

    // Steps condicionais podem sumir embaixo do usuário — ex.: ele declara 2
    // acompanhantes, avança até a aba do 2º e volta para trocar por 1. Como
    // wizardStep é posicional sobre visibleSteps, sem este clamp ele ficaria
    // apontando para um step inexistente.
    //
    // Só corrige quando o usuário estava mesmo numa aba do formulário: as
    // posições de facial/revisão/ingresso/pagamento ficam ACIMA de
    // visibleSteps.length e não podem ser tocadas.
    watch(() => visibleSteps.value.length, (newLength, oldLength) => {
      if (newLength >= oldLength) return;
      if (wizardStep.value > oldLength) return;
      if (wizardStep.value > newLength) {
        wizardStep.value = Math.max(minStep.value, newLength);
      }
    });

    // Steps exibidos no header (com scroll/deslizamento simples)
    const MAX_VISIBLE_STEPS = 5; // Máximo de steps totais visíveis (incluindo facial e revisão)

    // Todos os steps incluindo facial, revisão, ticket selection e checkout
    const allStepsForNavigation = computed(() => {
      // Cria cópias dos steps com índices únicos
      const steps = visibleSteps.value.map((step, index) => ({
        ...step,
        _originalIndex: index,
        _uniqueId: step.id || `step_${index}_${Date.now()}`
      }));

      // Adiciona step de reconhecimento facial se habilitado
      if (facialEnabled.value) {
        steps.push({
          id: 'step_facial_recognition',
          name: 'Reconhecimento',
          isFacialStep: true,
          _isFixedStep: true,
          _originalIndex: steps.length,
          _uniqueId: 'step_facial_recognition'
        });
      }

      // Adiciona step de revisão (se habilitado nas settings do formulário)
      const hasReviewStep = formSchema.value?.settings?.has_review_step !== false;
      if (hasReviewStep) {
        steps.push({
          id: 'step_review',
          name: 'Revisão',
          isReviewStep: true,
          _isFixedStep: true,
          _originalIndex: steps.length,
          _uniqueId: 'step_review'
        });
      }

      // Adiciona steps de pagamento se hasPayment é true
      if (hasPayment.value) {
        steps.push({
          id: 'step_ticket_selection',
          name: 'Ingresso',
          isTicketStep: true,
          _isFixedStep: true,
          _originalIndex: steps.length,
          _uniqueId: 'step_ticket_selection'
        });

        steps.push({
          id: 'step_checkout',
          name: 'Pagamento',
          isCheckoutStep: true,
          _isFixedStep: true,
          _originalIndex: steps.length,
          _uniqueId: 'step_checkout'
        });
      }

      return steps;
    });

    // Steps exibidos na navegação (janela deslizante)
    const displayedSteps = computed(() => {
      const totalSteps = allStepsForNavigation.value.length;

      if (totalSteps === 0) return [];

      // Se couber todos os steps, mostra todos
      if (totalSteps <= MAX_VISIBLE_STEPS) {
        return allStepsForNavigation.value;
      }

      // Usa janela deslizante que acompanha o step atual
      const currentIndex = wizardStep.value - 1; // wizardStep começa em 1

      let start, end;

      // Se está no início, mostra os primeiros N steps
      if (currentIndex < 2) {
        start = 0;
        end = MAX_VISIBLE_STEPS;
      }
      // Se está no final, mostra os últimos N steps
      else if (currentIndex >= totalSteps - 2) {
        start = Math.max(0, totalSteps - MAX_VISIBLE_STEPS);
        end = totalSteps;
      }
      // No meio, centraliza o step atual
      else {
        const halfWindow = Math.floor(MAX_VISIBLE_STEPS / 2);
        start = currentIndex - halfWindow;
        end = start + MAX_VISIBLE_STEPS;
      }

      // Retorna apenas o slice, sem remapear (já tem _originalIndex)
      const sliced = allStepsForNavigation.value.slice(start, end);

      // Remove duplicatas baseado em _uniqueId (garantia extra)
      const uniqueSteps = [];
      const seenIds = new Set();

      for (const step of sliced) {
        const uniqueId = step._uniqueId || step.id;
        if (!seenIds.has(uniqueId)) {
          seenIds.add(uniqueId);
          uniqueSteps.push(step);
        }
      }

      return uniqueSteps;
    });

    // Todos os campos visíveis
    const allVisibleFields = computed(() => {
      const allFields = [];

      // Se não tem steps (formulário simples), pega campos diretamente do fields
      if (!visibleSteps.value || visibleSteps.value.length === 0) {
        const directFields = fields.value || [];
        directFields.forEach(field => {
          if (shouldShowField(field, formData, allSchemaFields.value)) {
            allFields.push(field);
          }
        });
        return allFields;
      }

      // Se tem steps (modo wizard), pega campos de cada step
      visibleSteps.value.forEach(step => {
        if (step.fields) {
          step.fields.forEach(field => {
            if (shouldShowField(field, formData, allSchemaFields.value)) {
              allFields.push(field);
            }
          });
        }
      });
      return allFields;
    });

    // Gera regras de validação dinâmicas
    const validationRules = computed(() => {
      const rules = {};

      try {
        const fields = allVisibleFields.value || [];

        fields.forEach(field => {
          if (!field || !field.name) return;

          const fieldRules = {};

          // Obrigatório
          if (field.required) {
            // Para campos boolean (checkbox/terms), usar sameAs(true) pois required do Vuelidate aceita false como válido
            if (isCheckboxField(field) || isTermsAcceptanceField(field)) {
              fieldRules.accepted = helpers.withMessage(
                isTermsAcceptanceField(field)
                  ? 'Você deve ler e aceitar os termos para prosseguir.'
                  : `${field.label || 'Este campo'} é obrigatório`,
                sameAs(true)
              );
            } else {
              fieldRules.required = helpers.withMessage(
                `${field.label || 'Este campo'} é obrigatório`,
                required
              );
            }
          }

          // Email
          if (field.type === 'email') {
            fieldRules.email = helpers.withMessage(
              'E-mail inválido',
              email
            );
          }

          // CPF
          if (field.type === 'cpf') {
            fieldRules.cpfValid = helpers.withMessage(
              'CPF inválido',
              validateCPF
            );
          }

          // CNPJ / CPF+CNPJ
          if (field.type === 'cnpj' || field.type === 'cpf_cnpj' || field.type === 'document') {
            fieldRules.cnpjValid = helpers.withMessage(
              'CNPJ inválido',
              validateCNPJ
            );
          }

          // Telefone
          if (field.type === 'phone' || field.type === 'tel') {
            fieldRules.phoneValid = helpers.withMessage(
              'Telefone inválido',
              validatePhone
            );
          }

          // CEP
          if (field.type === 'cep') {
            fieldRules.cepValid = helpers.withMessage(
              'CEP inválido',
              validateCEP
            );
          }

          // Data (date / birthdate) — valida se é uma data real
          if (isDateField(field)) {
            fieldRules.validDate = helpers.withMessage(
              'Data inválida',
              (value) => {
                if (!value) return true;
                const match = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
                if (!match) return false;
                const [, dd, mm, yyyy] = match;
                const d = new Date(+yyyy, +mm - 1, +dd);
                return d.getFullYear() === +yyyy && d.getMonth() === +mm - 1 && d.getDate() === +dd;
              }
            );
          }

          // Data e Hora (datetime) — exige data + hora completas e válidas
          if (isDatetimeField(field)) {
            fieldRules.validDatetime = helpers.withMessage(
              'Informe data e hora completas (DD/MM/AAAA HH:MM)',
              (value) => {
                if (!value) return true;
                const m = String(value).match(/^(\d{2})\/(\d{2})\/(\d{4}) (\d{2}):(\d{2})$/);
                if (!m) return false;
                const [, dd, mm, yyyy, hh, min] = m;
                const d = new Date(+yyyy, +mm - 1, +dd);
                const dateOk = d.getFullYear() === +yyyy && d.getMonth() === +mm - 1 && d.getDate() === +dd;
                const timeOk = +hh <= 23 && +min <= 59;
                return dateOk && timeOk;
              }
            );
          }

          // Birthdate — deve ser no passado + min_age se configurado
          if (field.type === 'birthdate' || field.type === 'birth_date') {
            fieldRules.dateInPast = helpers.withMessage(
              'Data de nascimento deve ser anterior a hoje',
              (value) => {
                if (!value) return true;
                const match = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
                if (!match) return false;
                const [, dd, mm, yyyy] = match;
                return new Date(+yyyy, +mm - 1, +dd) < new Date();
              }
            );

            // min_age: lê do array de validations do schema
            const validations = Array.isArray(field.validations) ? field.validations : [];
            const minAgeRule = validations.find(v => v.rule === 'min_age');
            if (minAgeRule) {
              const minYears = minAgeRule.years || 18;
              fieldRules.minAge = helpers.withMessage(
                `Idade mínima: ${minYears} anos`,
                (value) => {
                  if (!value) return true;
                  const match = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
                  if (!match) return false;
                  const [, dd, mm, yyyy] = match;
                  const birth = new Date(+yyyy, +mm - 1, +dd);
                  const today = new Date();
                  let age = today.getFullYear() - birth.getFullYear();
                  const mDiff = today.getMonth() - birth.getMonth();
                  if (mDiff < 0 || (mDiff === 0 && today.getDate() < birth.getDate())) age--;
                  return age >= minYears;
                }
              );
            }

            // max_age: limite superior
            const maxAgeRule = validations.find(v => v.rule === 'max_age');
            if (maxAgeRule) {
              const maxYears = maxAgeRule.years || 120;
              fieldRules.maxAge = helpers.withMessage(
                `Idade máxima: ${maxYears} anos`,
                (value) => {
                  if (!value) return true;
                  const match = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
                  if (!match) return false;
                  const [, dd, mm, yyyy] = match;
                  const birth = new Date(+yyyy, +mm - 1, +dd);
                  const today = new Date();
                  let age = today.getFullYear() - birth.getFullYear();
                  const mDiff = today.getMonth() - birth.getMonth();
                  if (mDiff < 0 || (mDiff === 0 && today.getDate() < birth.getDate())) age--;
                  return age <= maxYears;
                }
              );
            }
          }

          // Validações do backend
          if (field.validations) {
            if (field.validations.min_length) {
              fieldRules.minLength = helpers.withMessage(
                `Mínimo de ${field.validations.min_length} caracteres`,
                minLength(field.validations.min_length)
              );
            }

            if (field.validations.max_length) {
              fieldRules.maxLength = helpers.withMessage(
                `Máximo de ${field.validations.max_length} caracteres`,
                maxLength(field.validations.max_length)
              );
            }

            // Regex customizado
            if (field.validations.pattern) {
              const regex = new RegExp(field.validations.pattern);
              fieldRules.pattern = helpers.withMessage(
                field.validations.pattern_message || 'Formato inválido',
                (value) => !value || regex.test(value)
              );
            }
          }

          if (Object.keys(fieldRules).length > 0) {
            rules[field.name] = fieldRules;
          }
        });
      } catch (error) {
        console.error('Error building validation rules:', error);
      }

      return rules;
    });

    // Vuelidate - inicialização com configuração segura
    const v$ = useVuelidate(validationRules, formData, { $autoDirty: false });

    // Verifica se revisão está habilitada
    const reviewEnabled = computed(() => {
      return formSchema.value?.settings?.has_review_step !== false;
    });

    // Total de steps incluindo facial recognition e revisão
    const totalStepsWithReview = computed(() => {
      let total = visibleSteps.value?.length || 0;
      if (facialEnabled.value) total++; // +1 para reconhecimento facial
      if (reviewEnabled.value) total++; // +1 para o step de revisão
      return total;
    });

    // Verifica se está no último step do formulário (antes do facial/revisão)
    const isLastFormStep = computed(() => {
      // Se tem facial enabled, último step é o anterior ao facial
      // Se não tem facial, último step é o anterior à revisão
      return wizardStep.value === (visibleSteps.value?.length || 0);
    });

    // Verifica se é um formulário wizard (com steps)
    const isWizardMode = computed(() => {
      const hasSteps = visibleSteps.value && visibleSteps.value.length > 0;

      // IMPORTANTE: Se tem pagamento, SEMPRE forçar modo wizard
      // Mesmo que o formulário não tenha steps, vamos criar um fluxo wizard
      const forceWizard = hasPayment.value && wizardStep.value > 0;

      return hasSteps || forceWizard;
    });

    // Step do reconhecimento facial (último antes da revisão)
    const facialStepIndex = computed(() => {
      if (!facialEnabled.value) return -1;
      return (visibleSteps.value?.length || 0) + 1;
    });

    // Verifica se está no step de reconhecimento facial
    const isFacialStep = computed(() => {
      if (!facialEnabled.value || !isWizardMode.value) return false;
      return wizardStep.value === facialStepIndex.value;
    });

    // Verifica se está no step de revisão
    const isReviewStep = computed(() => {
      if (!isWizardMode.value || !reviewEnabled.value) return false;
      return wizardStep.value === totalStepsWithReview.value;
    });

    // Index do ticket selection step (após revisão)
    const ticketStepIndex = computed(() => {
      if (!hasPayment.value) return -1;
      return totalStepsWithReview.value + 1;
    });

    // Verifica se está no step de seleção de ingresso
    const isTicketStep = computed(() => {
      if (!hasPayment.value || !isWizardMode.value) return false;
      return wizardStep.value === ticketStepIndex.value;
    });

    // Index do checkout step (após ticket selection)
    const checkoutStepIndex = computed(() => {
      if (!hasPayment.value) return -1;
      return totalStepsWithReview.value + 2;
    });

    // Verifica se está no step de checkout
    const isCheckoutStep = computed(() => {
      if (!hasPayment.value || !isWizardMode.value) return false;
      return wizardStep.value === checkoutStepIndex.value;
    });

    // Pode voltar (baseado no modo)
    const canGoBack = computed(() => {
      // Só permite voltar em modo wizard
      if (!isWizardMode.value) return false;
      // Em modo lista importada, pode voltar até step 0 (identificação)
      // Em modo inscrições abertas, pode voltar até step 1
      return wizardStep.value > minStep.value;
    });

    // Imagem de sucesso
    const successImage = computed(() => {
      try {
        return require('@/assets/sucess.png');
      } catch {
        return null;
      }
    });

    // Tela de sucesso
    const successScreen = computed(() => {
      return formSchema.value?.settings?.success_screen || formSchema.value?.success_screen || {
        title_suffix: 'inscrição foi realizada com sucesso!',
        message: 'Sua inscrição está sendo processada.',
        secondary_message: 'Acompanhe sua caixa de entrada para futuras atualizações sobre a sua inscrição e informações sobre o evento.'
      };
    });

    // Cor do loader baseada na customização
    const loaderColor = computed(() => {
      if (typeof window !== 'undefined') {
        const buttonColor = getComputedStyle(document.documentElement)
          .getPropertyValue('--button-color')
          .trim();
        return buttonColor || '#556ee6';
      }
      return '#556ee6';
    });

    // Inicialização
    onMounted(async () => {
      // Inicializa tracking passivo completo (registra pageview IMEDIATAMENTE)
      await initializeTracking();

      const waitForSettings = () => {
        return new Promise((resolve) => {
          if (settings.value) {
            resolve();
          } else {
            const unwatch = watch(settings, (newVal) => {
              if (newVal) {
                unwatch();
                resolve();
              }
            });
          }
        });
      };

      await waitForSettings();

      // Carregar configuração de pagamento (métodos, taxas, responsabilidade, public key)
      try {
        const config = await getPaymentConfig();
        if (config) {
          paymentConfig.value = config;
          if (config.pagarme_public_key) {
            configurePagarme('', config.pagarme_public_key);
          }
        }
      } catch (e) {
        console.error('Error loading payment config:', e);
      }

      await initializeForm();
    });

    // Cleanup dos listeners de tracking ao desmontar componente
    onBeforeUnmount(() => {
      cleanupTracking();
    });

    // Inicializa formulário baseado no modo
    const initializeForm = async () => {
      loading.value = true;

      try {
        if (!rsvpSettings.value?.registration_status) {
          currentStep.value = 'closed';
          return;
        }

        // Limite global atingido — bloqueia antes do identification step
        // (backend devolve is_sold_out calculado em LandingPageController::isEventSoldOut).
        if (rsvpSettings.value?.is_sold_out) {
          currentStep.value = 'sold_out';
          return;
        }

        // Carrega config do controle de inscrição
        try {
          const rcConfig = await getRegistrationControlConfig();
          if (rcConfig && rcConfig.enabled) {
            registrationControlConfig.value = rcConfig;
          }
        } catch (e) {
          // Silently ignore RC config errors
        }

        if (isImportedList.value) {
          // Lista importada: mostra identificação primeiro (step 0)
          minStep.value = 0;
          wizardStep.value = 0;
          showForm.value = true;
          currentStep.value = 'form';
        } else if (hasPayment.value) {
          // Inscrição aberta OU RC, COM pagamento:
          // Mostra step 0 de identificação por email antes do form
          minStep.value = 0;
          wizardStep.value = 0;
          showForm.value = true;
          currentStep.value = 'form';
        } else if (registrationControlConfig.value) {
          // Inscrições abertas COM controle de inscrição (sem pagamento):
          // Mostra a tela de validação primeiro, sem carregar o formulário ainda
          minStep.value = 1;
          showForm.value = true;
          currentStep.value = 'form';
        } else {
          // Inscrições abertas SEM controle e SEM pagamento: carrega formulário direto
          minStep.value = 1;
          await loadGlobalForm();
        }
      } catch (error) {
        console.error('Erro ao inicializar formulário:', error);
        identificationError.value = 'Erro ao carregar formulário. Tente novamente.';
      } finally {
        loading.value = false;
      }
    };

    // Desduplicar nomes de campos no schema (ex: vários "aceite_termos" viram aceite_termos, aceite_termos_2, etc.)
    const deduplicateFieldNames = (schema) => {
      if (!schema) return;
      const allFields = [];
      if (schema.fields) allFields.push(...schema.fields);
      if (schema.steps) {
        schema.steps.forEach(step => {
          if (step.fields) allFields.push(...step.fields);
        });
      }

      const nameCount = {};
      allFields.forEach(field => {
        if (!field || !field.name) return;
        const baseName = field.name;
        if (!nameCount[baseName]) {
          nameCount[baseName] = 1;
        } else {
          nameCount[baseName]++;
          const uniqueName = baseName + '_' + nameCount[baseName];
          fieldNameMap[uniqueName] = baseName;
          field.name = uniqueName;
        }
      });
    };

    // Inicializa formData para campos multiselect (precisam de array)
    const initMultiselectFields = (schema) => {
      if (!schema) return;
      const allFields = [];
      if (schema.fields) allFields.push(...schema.fields);
      if (schema.steps) {
        schema.steps.forEach(step => {
          if (step.fields) allFields.push(...step.fields);
        });
      }
      const selectTypes = ['select', 'dropdown', 'gender', 'state'];
      const selectComponents = ['SelectField', 'StateField'];
      allFields.forEach(field => {
        if (field.type === 'multiselect' || field.component === 'MultiselectField') {
          if (!Array.isArray(formData[field.name])) {
            formData[field.name] = [];
          }
        } else if (selectTypes.includes(field.type) || selectComponents.includes(field.component)) {
          if (formData[field.name] === undefined) {
            formData[field.name] = '';
          }
        } else if (isCheckboxField(field) || isTermsAcceptanceField(field)) {
          if (formData[field.name] === undefined) {
            formData[field.name] = false;
          }
        }
      });
    };

    // Carrega formulário global (inscrições abertas)
    const loadGlobalForm = async (formIdParam = null) => {
      // Passa o RC item identificado (se houver) para que o backend remova do
      // schema os campos restritos por tag que não se aplicam a este participante.
      const response = await getGlobalFormSchema(formIdParam, registrationControlItemId.value);
      formSchema.value = response.data;
      formId.value = response.data?.id;
      groupId.value = response.group_id;
      deduplicateFieldNames(response.data);
      initMultiselectFields(response.data);

      // Carrega configuração de permissão de edição
      allowDataChangeBeforeConfirmation.value = response.allow_data_change_before_confirmation || false;

      // Carrega configuração de reconhecimento facial
      if (response.enable_facial_recognition !== undefined) {
        formSchema.value.enable_facial_recognition = response.enable_facial_recognition;
      }

      // Define wizard step inicial
      // Se tem pagamento, sempre inicia em step 1 para ativar modo wizard
      wizardStep.value = 1;
      showForm.value = true;
      currentStep.value = 'form';
    };

    // Retoma pagamento pendente (usado por modos 1, 2 e 3)
    const handleResumePaymentFlow = async (response) => {
      participant.value = response.participant;
      hasPayment.value = true;

      // Carrega form schema (necessário para calcular ticketStepIndex/checkoutStepIndex)
      if (!formSchema.value) {
        await loadGlobalForm();
      }

      const status = response.registration_status;

      // Já completou
      if (status === 'completed') {
        showThanks.value = true;
        loading.value = false;
        return;
      }

      // Tem pending order válido?
      if (response.pending_order && response.pending_order_expires_at) {
        const expired = new Date(response.pending_order_expires_at) < new Date();

        if (!expired && response.pending_order.ticket_id) {
          // Order válido — restaurar e ir direto pro checkout
          selectedTicket.value = {
            id: response.pending_order.ticket_id,
            name: response.pending_order.ticket_name || response.pending_order.name,
            value: response.pending_order.unit_price,
          };
          ticketQuantity.value = response.pending_order.quantity || 1;
          ticketTotal.value = response.pending_order.total;

          wizardStep.value = checkoutStepIndex.value;
          minStep.value = ticketStepIndex.value;
          loading.value = false;
          return;
        }
      }

      // Sem order válido ou expirado — ir pra seleção de ingresso
      wizardStep.value = ticketStepIndex.value;
      minStep.value = ticketStepIndex.value;
      loading.value = false;
    };

    // Helper: verifica se campo é email
    const isEmailField = (field) => {
      if (!field) return false;
      return field.type === 'email' ||
             field.name === 'email' ||
             field.fieldType?.name === 'email';
    };

    // Identifica participante (lista importada ou inscrição aberta com pagamento)
    const handleIdentification = async () => {
      identificationSubmitted.value = true;

      if (!identificationValue.value) {
        return;
      }

      loading.value = true;
      identificationError.value = null;

      try {
        if (isImportedList.value) {
          // ═══ FLUXO EXISTENTE: lista importada ═══
          const payload = {
            [identificationType.value]: identificationValue.value
          };

          const response = await identifyAndGetForm(payload);

          if (response.ok) {
            // Detectar pagamento pendente
            if (hasPayment.value && response.resume_payment) {
              await handleResumePaymentFlow(response);
              return;
            }

            participant.value = response.participant;
            formSchema.value = response.form;
            formId.value = response.form?.id;
            groupId.value = response.group_id;
            deduplicateFieldNames(response.form);
            initMultiselectFields(response.form);

            // Atualiza permissões de edição
            canEditData.value = response.can_edit_data !== undefined ? response.can_edit_data : true;
            allowDataChangeBeforeConfirmation.value = response.allow_data_change_before_confirmation || false;
            canDecline.value = response.can_decline || false;
            declineOnly.value = response.decline_only || false;

            // Carrega configuração de reconhecimento facial
            if (response.enable_facial_recognition !== undefined) {
              formSchema.value.enable_facial_recognition = response.enable_facial_recognition;
            }

            // Preenche dados existentes
            if (response.existing_data) {
              Object.assign(formData, response.existing_data);
            }

            // Preenche dados do participante
            if (response.participant) {
              if (response.participant.name) formData.name = response.participant.name;
              if (response.participant.email) formData.email = response.participant.email;
              if (response.participant.phone) formData.phone = response.participant.phone;
              if (response.participant.cpf) formData.cpf = response.participant.cpf;
            }

            // Após identificar, muda minStep para 1
            minStep.value = 1;
            wizardStep.value = 1;
          } else {
            identificationError.value = response.message || 'Participante não encontrado.';
          }

        } else {
          // ═══ FLUXO NOVO: inscrição aberta/RC com pagamento ═══
          const response = await checkParticipantByEmail(identificationValue.value);

          if (response.exists && response.resume_payment) {
            // Participante retornando com pagamento pendente
            await handleResumePaymentFlow(response);
            return;
          }

          if (response.exists && response.completed) {
            // Já inscrito
            identificationError.value = response.message;
            loading.value = false;
            return;
          }

          // Participante novo — guardar email e prosseguir
          identifiedEmail.value = identificationValue.value;

          if (registrationControlConfig.value) {
            // Tem RC: avança pro step de validação RC, form carrega depois
            wizardStep.value = 1;
            minStep.value = 1;
          } else {
            // Sem RC: carrega form schema e avança
            await loadGlobalForm();
            formData.email = identifiedEmail.value;
            wizardStep.value = 1;
            minStep.value = 1;
          }
        }
      } catch (error) {
        console.error('Erro na identificação:', error);

        if (error.response?.status === 404) {
          identificationError.value = error.response?.data?.message || 'Participante não encontrado na lista de convidados.';
        } else if (error.response?.status === 409) {
          identificationError.value = error.response?.data?.message || 'Sua inscrição já foi realizada.';
        } else {
          identificationError.value = 'Erro ao verificar dados. Tente novamente.';
        }
      } finally {
        loading.value = false;
      }
    };

    // Labels dos steps
    const getAbbreviatedLabel = (step) => {
      if (!step) return 'Dados';
      const name = step.name || 'Dados';
      if (name.length > 15) {
        return name.substring(0, 12) + '...';
      }
      return name;
    };

    // Helpers para campos
    const getVisibleFields = (step) => {
      if (!step || !step.fields) return [];
      return step.fields.filter(f => shouldShowField(f, formData, allSchemaFields.value));
    };

    // Obtém opções do campo (incluindo estados)
    const getFieldOptions = (field) => {
      if (!field) return [];
      if (field.type === 'state' || field.component === 'StateField' || field.name === 'state') {
        return brazilianStates;
      }
      // "Levará quantos acompanhantes?": as opções vêm do form builder com o
      // máximo configurado, mas o controle de inscrição pode ter menos vagas.
      // Sem este corte a pessoa escolheria 2, o filtro por vagas em visibleSteps
      // eliminaria a aba, e ela sumiria sem explicação.
      if (field.name === COMPANION_COUNT_FIELD) {
        const max = effectiveMaxCompanions.value;
        return (field.options || []).filter(opt => Number(opt?.value) <= max);
      }
      return field.options || [];
    };

    // Verifica se é um campo de seleção múltipla
    const isMultiselectField = (field) => {
      if (!field) return false;
      return field.type === 'multiselect' || field.component === 'MultiselectField';
    };

    // Verifica se é um campo de seleção (select, dropdown, etc)
    const isSelectField = (field) => {
      if (!field) return false;
      if (isMultiselectField(field)) return false;
      const selectTypes = ['select', 'dropdown', 'gender', 'state'];
      const selectComponents = ['SelectField', 'StateField'];
      return selectTypes.includes(field.type) || selectComponents.includes(field.component);
    };

    // Verifica se é um campo de radio
    const isRadioField = (field) => {
      if (!field) return false;
      return field.type === 'radio' || field.component === 'RadioField';
    };

    // Verifica se é um campo de checkbox
    const isCheckboxField = (field) => {
      if (!field) return false;
      const checkTypes = ['checkbox', 'checkbox_single', 'toggle', 'terms'];
      const checkComponents = ['CheckboxField', 'CheckboxSingleField', 'ToggleField', 'TermsField'];
      return checkTypes.includes(field.type) || checkComponents.includes(field.component);
    };

    // Verifica se o campo terms tem link configurado
    const hasTermsLink = (field) => {
      return field?.config?.link_text && field?.config?.link_url;
    };

    // Retorna o texto do checkbox (field.label é o padrão, como no template original)
    const getCheckboxLabel = (field) => {
      return field?.label || field?.options?.[0]?.label || field?.help_text || '';
    };

    // Renderiza o label do checkbox com link clicável nos termos
    const renderCheckboxLabel = (field) => {
      const label = getCheckboxLabel(field);
      const requiredMark = field?.required ? ' <span style="color: #ef4444;">*</span>' : '';
      if (!hasTermsLink(field)) {
        return label + requiredMark;
      }
      const linkText = field.config.link_text;
      const linkUrl = field.config.link_url;
      const escaped = linkText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const regex = new RegExp(escaped, 'i');
      const match = label.match(regex);
      if (match) {
        return label.replace(regex, `<a href="${linkUrl}" target="_blank" rel="noopener" onclick="event.stopPropagation()" style="color: #3b82f6; text-decoration: underline;">${match[0]}</a>`) + requiredMark;
      }
      return `${label} <a href="${linkUrl}" target="_blank" rel="noopener" onclick="event.stopPropagation()" style="color: #3b82f6; text-decoration: underline;">${linkText}</a>${requiredMark}`;
    };

    // Verifica se é um campo de aceite com texto longo
    const isTermsAcceptanceField = (field) => {
      if (!field) return false;
      return field.type === 'terms_acceptance' ||
             field.component === 'TermsAcceptanceField' ||
             field.fieldType?.name === 'terms_acceptance';
    };

    // Verifica se é um bilhete fake (display-only)
    const isFakeTicketField = (field) => {
      if (!field) return false;
      return field.type === 'fake_ticket' ||
             field.component === 'FakeTicketField' ||
             field.fieldType?.name === 'fake_ticket' ||
             field.field_type?.name === 'fake_ticket' ||
             field.config?.type === 'fake_ticket';
    };

    // Verifica se é um disclaimer (display-only)
    const isDisclaimerField = (field) => {
      if (!field) return false;
      return field.type === 'disclaimer' ||
             field.component === 'DisclaimerField' ||
             field.fieldType?.name === 'disclaimer' ||
             field.field_type?.name === 'disclaimer' ||
             field.config?.type === 'disclaimer';
    };

    // Verifica se é um bloco de instruções (display-only)
    const isInstrucoesField = (field) => {
      if (!field) return false;
      return field.type === 'instrucoes' ||
             field.component === 'InstrucoesField' ||
             field.fieldType?.name === 'instrucoes' ||
             field.field_type?.name === 'instrucoes' ||
             field.config?.type === 'instrucoes';
    };

    // Instruções com aceite obrigatório (precisa ser marcado para prosseguir)
    const isInstrucoesAcceptanceField = (field) => {
      return isInstrucoesField(field) && !!(field.config && field.config.require_acceptance);
    };

    // Verifica se é um campo de arquivo
    const isFileField = (field) => {
      if (!field) return false;
      const fileTypes = ['file', 'image'];
      const fileComponents = ['FileField', 'ImageField'];
      return fileTypes.includes(field.type) || fileComponents.includes(field.component);
    };

    // Verifica se é um campo de textarea
    const isTextareaField = (field) => {
      if (!field) return false;
      // Nunca tratar campos de email como textarea
      if (field.type === 'email' || field.name?.includes('email')) return false;
      return field.type === 'textarea' || field.component === 'TextareaField';
    };

    // Verifica se é um campo de data
    const isDateField = (field) => {
      if (!field) return false;
      return field.type === 'date' || field.type === 'birthdate' || field.component === 'DateField';
    };

    // Campo de data + hora (input datetime-local nativo)
    const isDatetimeField = (field) => {
      if (!field) return false;
      return field.type === 'datetime' || field.component === 'DatetimeField';
    };

    // Verifica se é um campo de texto
    // Tipos explícitos que nunca devem ser tratados como phone/cpf/cnpj/cep pelo fallback de nome
    const EXPLICIT_TYPES = ['select', 'dropdown', 'multiselect', 'radio', 'checkbox',
      'gender', 'state', 'autocomplete', 'textarea', 'date', 'birthdate', 'datetime',
      'file', 'image', 'terms', 'terms_acceptance', 'toggle', 'checkbox_single'];

    const isPhoneField = (field) => {
      if (!field) return false;
      const type = field.type || field.fieldType?.name || field.config?.type;
      if (type === 'phone' || type === 'tel' || type === 'telefone') return true;
      if (EXPLICIT_TYPES.includes(type)) return false;
      return !!(field.name && (field.name.includes('phone') || field.name.includes('telefone')));
    };

    const isCpfField = (field) => {
      if (!field) return false;
      const type = field.type || field.fieldType?.name || field.config?.type;
      if (type === 'cpf') return true;
      if (EXPLICIT_TYPES.includes(type)) return false;
      return !!(field.name && field.name.includes('cpf') && !field.name.includes('cnpj'));
    };

    const isCnpjField = (field) => {
      if (!field) return false;
      const type = field.type || field.fieldType?.name || field.config?.type;
      if (type === 'cnpj' || type === 'cpf_cnpj' || type === 'document') return true;
      if (EXPLICIT_TYPES.includes(type)) return false;
      return !!(field.name && field.name.includes('cnpj'));
    };

    const isCepField = (field) => {
      if (!field) return false;
      const type = field.type || field.fieldType?.name || field.config?.type;
      if (type === 'cep') return true;
      if (EXPLICIT_TYPES.includes(type)) return false;
      return !!(field.name && field.name.includes('cep'));
    };

    const isTextField = (field) => {
      if (!field) return false;
      // Se for outro tipo específico, não é texto
      if (isSelectField(field) || isMultiselectField(field) || isRadioField(field) || isCheckboxField(field) ||
          isTermsAcceptanceField(field) || isFakeTicketField(field) || isDisclaimerField(field) || isInstrucoesField(field) || isFileField(field) || isTextareaField(field) || isDateField(field) || isDatetimeField(field) ||
          isPhoneField(field) || isCpfField(field) || isCnpjField(field) || isCepField(field)) {
        return false;
      }
      // Section divider não é campo
      if (field.type === 'section' || field.component === 'SectionDivider') {
        return false;
      }
      // Todo o resto é tratado como texto
      return true;
    };

    const getInputType = (field) => {
      if (!field) return 'text';
      const typeMap = {
        'email': 'email',
        'phone': 'tel',
        'cpf': 'text',
        'cnpj': 'text',
        'cep': 'text',
        'date': 'date',
        'birthdate': 'date',
        'time': 'time',
        'datetime': 'datetime-local',
        'number': 'number',
        'decimal': 'number',
        'percentage': 'number',
        'password': 'password',
        'url': 'url'
      };
      return typeMap[field.type] || 'text';
    };

    // Verifica se campo deve ser readonly (não pode editar dados após confirmação)
    const isFieldReadonly = (field) => {
      if (!field) return false;
      // Se o campo já tem readonly definido, mantém
      if (field.readonly) return true;
      // Se não pode editar dados (participante já confirmado ou configuração desabilitada)
      if (!canEditData.value) return true;
      // Campo email readonly quando veio do step de identificação
      if (identifiedEmail.value && isEmailField(field)) return true;
      return false;
    };

    // Verifica se campo deve ser disabled
    const isFieldDisabled = (field) => {
      if (!field) return false;
      // Se o campo já tem disabled definido, mantém
      if (field.disabled) return true;
      // Para campos select, usar disabled ao invés de readonly
      // quando não pode editar dados
      if (!canEditData.value && isSelectField(field)) return true;
      return false;
    };

    // Classe do container do campo baseado no layout
    const getFieldContainerClass = (field) => {
      if (!field) return 'w-full mb-4 px-2';

      const cols = String(field.layout?.cols || '12');

      // Mapeamento direto de cols para classes Tailwind
      const colsToClass = {
        '1': 'w-full md:w-1/12 mb-4 px-2',
        '2': 'w-full md:w-2/12 mb-4 px-2',
        '3': 'w-full md:w-1/4 mb-4 px-2',
        '4': 'w-full md:w-1/3 mb-4 px-2',
        '5': 'w-full md:w-5/12 mb-4 px-2',
        '6': 'w-full md:w-1/2 mb-4 px-2',
        '7': 'w-full md:w-7/12 mb-4 px-2',
        '8': 'w-full md:w-2/3 mb-4 px-2',
        '9': 'w-full md:w-3/4 mb-4 px-2',
        '10': 'w-full md:w-10/12 mb-4 px-2',
        '11': 'w-full md:w-11/12 mb-4 px-2',
        '12': 'w-full mb-4 px-2'
      };

      return colsToClass[cols] || 'w-full mb-4 px-2';
    };

    // Validação assíncrona de campo (unicidade de email, cpf, etc.)
    // Normaliza valor para comparação de unicidade — espelha
    // FormValidationService::normalizeForCompare no backend.
    const normalizeUniqueValue = (value, field) => {
      if (value === null || value === undefined) return '';
      const type = field?.type || field?.fieldType?.name;
      if (['cpf', 'cnpj', 'cpf_cnpj', 'phone', 'cep'].includes(type)) {
        return String(value).replace(/\D/g, '');
      }
      if (type === 'email') {
        return String(value).trim().toLowerCase();
      }
      return String(value).trim();
    };

    // Checa colisão cross-payload (principal × acompanhante ou companion × companion).
    // Espelha FormValidationService::checkCrossPayloadUniqueness. Roda local, sem hit no servidor.
    const checkCrossPayloadDuplicate = (field, value) => {
      if (!field || (!field.unique && field.type !== 'email' && field.type !== 'cpf')) return null;
      const normalized = normalizeUniqueValue(value, field);
      if (!normalized) return null;

      const allFields = allVisibleFields.value || [];
      const isStandardSlot = field.type === 'cpf' || field.type === 'email';

      for (const other of allFields) {
        if (!other || other.name === field.name) continue;
        // Mesmo slot conceitual:
        //  - Standard (cpf/email): match por field.type — o "slot" é a coluna conceitual.
        //  - Custom com unique: match por nome puro (_originalName ou name).
        const sameStandard = isStandardSlot && other.type === field.type;
        const ownName = field._originalName || field.name;
        const otherName = other._originalName || other.name;
        const sameCustom = field.unique && other.unique && ownName === otherName;
        if (!sameStandard && !sameCustom) continue;

        const otherValue = formData[other.name];
        if (otherValue === null || otherValue === undefined || otherValue === '') continue;
        if (normalizeUniqueValue(otherValue, other) !== normalized) continue;

        // Identifica de quem é o conflito para mensagem amigável
        const otherIsMain = !other._originalName;
        const label = field.label || 'valor';
        if (otherIsMain) {
          return `Este ${label} deve ser diferente do informado pelo participante principal.`;
        }
        const m = other.name.match(/^companion_(\d+)_/);
        const otherIdx = m ? parseInt(m[1], 10) : 0;
        return `Este ${label} deve ser diferente do acompanhante ${otherIdx}.`;
      }
      return null;
    };

    const validateFieldAsync = async (field) => {
      if (!field || !field.name || !formId.value) return;

      // Só valida campos com constraint unique ou tipo email/cpf
      const isUniqueField = field.unique || field.type === 'email' || field.type === 'cpf';
      if (!isUniqueField) return;

      const value = formData[field.name];
      if (!value || !value.toString().trim()) {
        delete asyncErrors[field.name];
        return;
      }

      // Para email, verifica formato antes de chamar API
      if (field.type === 'email') {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) return;
      }

      // Para CPF, verifica se tem 11 dígitos antes de chamar API
      if (field.type === 'cpf') {
        const digits = value.replace(/\D/g, '');
        if (digits.length < 11) return;
      }

      // Checagem local cross-payload primeiro — feedback imediato sem ida ao servidor
      // quando o valor colide com outro campo do mesmo form (principal × acompanhante etc).
      const crossError = checkCrossPayloadDuplicate(field, value);
      if (crossError) {
        asyncErrors[field.name] = crossError;
        return;
      }

      // Companion steps prefixam o name (companion_N_*) — o backend busca o field
      // por name na tabela form_fields, então enviamos o _originalName (sem prefixo)
      // para que a regra unique do FormField rode. A chave de erro continua sendo
      // field.name (com prefixo), pra que a UI mostre o erro no input certo.
      const lookupName = field._originalName || field.name;

      try {
        asyncValidating[field.name] = true;
        const context = {};
        if (participant.value?.id) {
          context.participant_id = participant.value.id;
        }

        const result = await validateFieldGlobal(formId.value, lookupName, value, context);

        if (result && !result.valid && result.errors && result.errors.length > 0) {
          asyncErrors[field.name] = result.errors[0];
        } else {
          delete asyncErrors[field.name];
        }
      } catch (err) {
        // Em falha de rede, não bloqueia — o submit final vai pegar
        delete asyncErrors[field.name];
      } finally {
        delete asyncValidating[field.name];
      }
    };

    // Marca campo como tocado
    const touchField = (fieldName) => {
      touchedFields.value[fieldName] = true;
      if (v$ && v$.value && v$.value[fieldName]) {
        v$.value[fieldName].$touch();
      }
      // Limpa erro geral quando usuário começar a editar
      if (formError.value) {
        formError.value = null;
      }

      // Dispara validação assíncrona para campos unique
      const field = allVisibleFields.value?.find(f => f.name === fieldName);
      if (field) {
        validateFieldAsync(field);
      }
    };

    // Executa behaviors do campo quando o valor muda (ex: CEP → autofill endereço)
    const onFieldInput = (field) => {
      if (!field?.behaviors || !Array.isArray(field.behaviors) || field.behaviors.length === 0) return;
      const value = formData[field.name];
      // Prioriza campos do step atual para que findTargetField ache os campos corretos
      // (ex: cidade_2 no step 2 em vez de cidade no step 1)
      let fieldsForBehavior = allVisibleFields.value || [];
      if (visibleSteps.value && visibleSteps.value.length > 0 && wizardStep.value > 0) {
        const currentStepData = visibleSteps.value[wizardStep.value - 1];
        if (currentStepData?.fields) {
          const stepFields = currentStepData.fields.filter(f => shouldShowField(f, formData, allSchemaFields.value));
          if (stepFields.length > 0) {
            fieldsForBehavior = stepFields;
          }
        }
      }
      executeBehaviors(field, value, fieldsForBehavior, (name, val) => {
        formData[name] = val;
      });
    };

    // Verifica se campo tem erro
    const hasFieldError = (field) => {
      if (!field || !field.name) return false;

      // Verifica erro assíncrono (email/cpf duplicado)
      if (asyncErrors[field.name]) return true;

      // Verifica se v$ está inicializado
      if (!v$ || !v$.value) return false;

      const fieldValidator = v$.value[field.name];
      if (!fieldValidator) {
        // Fallback para campos não no vuelidate (como file)
        if (field.required && isFileField(field)) {
          return (formSubmitted.value || touchedFields.value[field.name]) && !files.value[field.name];
        }
        if (field.required && (isCheckboxField(field) || isTermsAcceptanceField(field))) {
          return (formSubmitted.value || touchedFields.value[field.name]) && formData[field.name] !== true;
        }
        // Instruções com aceite — obrigatório por config, independente do flag `required`
        if (isInstrucoesAcceptanceField(field)) {
          return (formSubmitted.value || touchedFields.value[field.name]) && formData[field.name] !== true;
        }
        return false;
      }

      // Mostra erro se campo foi tocado ou formulário foi submetido
      return (touchedFields.value[field.name] || formSubmitted.value) && fieldValidator.$error;
    };

    // Obtém mensagem de erro do campo
    const getFieldErrorMessage = (field) => {
      if (!field || !field.name) return '';

      // Prioriza erro assíncrono (email/cpf duplicado)
      if (asyncErrors[field.name]) return asyncErrors[field.name];

      // Verifica se v$ está inicializado
      if (!v$ || !v$.value) return '';

      const fieldValidator = v$.value[field.name];
      if (!fieldValidator) {
        // Fallback para campos sem vuelidate
        if (field.required && isFileField(field) && !files.value[field.name]) {
          return 'Por favor, anexe o arquivo solicitado.';
        }
        if (field.required && isTermsAcceptanceField(field) && formData[field.name] !== true) {
          return 'Você deve ler e aceitar os termos para prosseguir.';
        }
        if (field.required && isCheckboxField(field) && formData[field.name] !== true) {
          return field.type === 'terms' ? 'Você deve concordar com os termos para prosseguir.' : 'Campo obrigatório';
        }
        if (isInstrucoesAcceptanceField(field) && formData[field.name] !== true) {
          return 'Você precisa marcar o aceite das instruções para prosseguir.';
        }
        return '';
      }

      if (fieldValidator.$errors && fieldValidator.$errors.length > 0) {
        return fieldValidator.$errors[0].$message;
      }

      return '';
    };

    // File refs management
    const setFileRef = (fieldName, el) => {
      if (el) {
        fileRefs.value[fieldName] = el;
      }
    };

    // File handling
    const triggerFileInput = (fieldName) => {
      const fileInput = fileRefs.value[fieldName];
      if (fileInput) {
        fileInput.click();
      }
    };

    const onFileChange = (fieldName, event) => {
      const file = event.target.files[0];
      if (!file) return;

      // Busca max_size: usa validations (form builder) como fonte de verdade, default 20MB
      const field = allVisibleFields.value?.find(f => f.name === fieldName);
      const validations = Array.isArray(field?.validations) ? field.validations : [];
      const fileSizeRule = validations.filter(v => v.rule === 'file_size').pop();
      const maxSizeKb = fileSizeRule?.max_kb || 20480;
      const maxSizeBytes = maxSizeKb * 1024;

      if (file.size > maxSizeBytes) {
        const maxMb = (maxSizeKb / 1024).toFixed(0);
        formError.value = `O arquivo "${file.name}" excede o tamanho máximo permitido de ${maxMb}MB.`;
        event.target.value = '';
        scrollToFormRoot();
        return;
      }

      formError.value = null;
      files.value[fieldName] = file;
      formData[fieldName] = file;
      touchField(fieldName);
    };

    const clearFile = (fieldName) => {
      files.value[fieldName] = null;
      formData[fieldName] = null;
    };

    // Validação por step
    const validateCurrentStep = () => {
      formSubmitted.value = true;

      // Bloqueia se há validação assíncrona em andamento (email/cpf sendo verificado)
      if (Object.keys(asyncValidating).length > 0) {
        return false;
      }

      try {
        // Se não é modo wizard, valida todos os campos
        if (!isWizardMode.value) {
          const fieldsToValidate = allVisibleFields.value;

          // Toca todos os campos
          fieldsToValidate.forEach(field => {
            if (field && field.name) {
              touchField(field.name);
            }
          });

          // Verifica erros
          let hasErrors = false;
          fieldsToValidate.forEach(field => {
            if (hasFieldError(field)) {
              hasErrors = true;
            }
          });

          return !hasErrors;
        }

        // Modo wizard: valida apenas o step atual
        const stepsArr = visibleSteps.value;
        if (!stepsArr || !Array.isArray(stepsArr)) return true;

        const currentStepData = stepsArr[wizardStep.value - 1];
        if (!currentStepData) return true;

        const stepFields = getVisibleFields(currentStepData);
        if (!stepFields || !stepFields.length) return true;

        // Toca todos os campos do step atual
        stepFields.forEach(field => {
          if (field && field.name) {
            touchField(field.name);
          }
        });

        // Verifica erros
        let hasErrors = false;
        stepFields.forEach(field => {
          if (hasFieldError(field)) {
            hasErrors = true;
          }
        });

        return !hasErrors;
      } catch (error) {
        console.error('Error in validateCurrentStep:', error);
        return true;
      }
    };

    // Formata valor do campo para exibição na revisão
    const getFieldDisplayValue = (field) => {
      if (!field || !field.name) return '';

      const value = formData[field.name];
      if (!value && value !== 0 && value !== false) return '-';

      // Checkbox / Terms Acceptance
      if (isCheckboxField(field) || isTermsAcceptanceField(field)) {
        return value === true ? 'Sim' : 'Não';
      }

      // Multiselect - exibe labels separados por vírgula
      if (isMultiselectField(field) && Array.isArray(value)) {
        const options = getFieldOptions(field);
        return value.map(v => {
          const opt = options.find(o => o.value === v);
          return opt?.label || v;
        }).join(', ');
      }

      // Select/Radio - tenta pegar o label da opção
      if (isSelectField(field) || isRadioField(field)) {
        const options = getFieldOptions(field);
        const option = options.find(opt => opt.value === value);
        return option?.label || value;
      }

      // Data (valor já está em DD/MM/YYYY com a máscara)
      if (isDateField(field)) {
        return value;
      }

      return value;
    };

    // Captura o blob da selfie facial
    const handleFacialCapture = (blob) => {
      facialBlob.value = blob;
    };

    // Refs do DOM
    const formRootRef = ref(null);

    // Scroll helpers
    const scrollToElement = (el) => {
      nextTick(() => {
        if (el) {
          const rect = el.getBoundingClientRect();
          const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
          window.scrollTo({ top: scrollTop + rect.top - 10, behavior: 'smooth' });
        }
      });
    };

    const scrollToFormRoot = () => {
      scrollToElement(formRootRef.value);
    };

    // Próximo step
    const scrollToFormTop = () => {
      nextTick(() => {
        const el = document.getElementById('step-nav');
        if (el) {
          const rect = el.getBoundingClientRect();
          const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
          window.scrollTo({ top: scrollTop + rect.top - 20, behavior: 'smooth' });
        } else {
          scrollToFormRoot();
        }
      });
    };

    const nextStep = () => {
      // Se está no step facial e não capturou a selfie, impede avançar
      if (isFacialStep.value && !facialBlob.value) {
        return;
      }

      // Se está no step de revisão, não faz validação
      if (!isReviewStep.value && !isFacialStep.value && !validateCurrentStep()) {
        scrollToFormTop();
        return;
      }

      const totalSteps = visibleSteps.value?.length || 0;

      // Se está no último step do formulário
      if (wizardStep.value === totalSteps && !facialEnabled.value) {
        if (reviewEnabled.value) {
          // Vai para revisão
          wizardStep.value++;
          formSubmitted.value = false;
        } else if (!hasPayment.value) {
          // Sem revisão e sem pagamento: submit direto
          handleSubmit();
          return;
        } else {
          // Sem revisão mas com pagamento: registra e vai para ticket/checkout
          registerParticipant();
          return;
        }
      } else {
        wizardStep.value++;
        formSubmitted.value = false;
      }

      // Scroll to top
      scrollToFormTop();
    };

    // Step anterior
    const prevStep = () => {
      if (wizardStep.value > minStep.value) {
        wizardStep.value--;
        formSubmitted.value = false;
      }
    };

    // === PAYMENT HANDLERS ===

    // Handler quando ticket é selecionado
    const handleTicketSelected = (ticketData) => {
      selectedTicket.value = ticketData.ticket;
      ticketQuantity.value = ticketData.quantity || 1;

      // Calcula o total
      const unitPrice = ticketData.ticket?.value || ticketData.ticket?.amount_ticket || 0;
      ticketTotal.value = unitPrice * ticketQuantity.value;
    };

    const handleTicketContinue = (ticketData) => {
      // Atualiza estado caso venha dados no continue
      if (ticketData?.ticket) {
        selectedTicket.value = ticketData.ticket;
        ticketQuantity.value = ticketData.quantity || 1;
        const unitPrice = ticketData.ticket?.value || ticketData.ticket?.amount_ticket || 0;
        ticketTotal.value = unitPrice * ticketQuantity.value;
      }

      // Avança para checkout
      wizardStep.value = checkoutStepIndex.value;
      scrollToFormTop();
    };

    // Volta para seleção de ingresso
    const goBackToTicketSelection = () => {
      wizardStep.value = ticketStepIndex.value;
      selectedTicket.value = null;
      ticketQuantity.value = 1;
      ticketTotal.value = 0;
    };

    // Handler quando PIX é gerado
    const handlePixGenerated = async (data) => {
      pixData.value = data;
      paymentProcessing.value = true;
      registrationStatus.value = 'payment_processing';

      // Registra PIX no backend
      try {
        await api.post(`/participants/${participant.value.id}/payment-initiated`, {
          payment_method: 'pix',
          payment_data: data
        });
      } catch (error) {
        console.error('❌ Error registering PIX:', error);
      }
    };

    // Handler quando Boleto é gerado
    const handleBoletoGenerated = async (data) => {
      boletoData.value = data;
      paymentProcessing.value = true;
      registrationStatus.value = 'payment_processing';

      // Registra Boleto no backend
      try {
        await api.post(`/participants/${participant.value.id}/payment-initiated`, {
          payment_method: 'boleto',
          payment_data: data
        });
      } catch (error) {
        console.error('Error registering boleto:', error);
      }
    };

    // Handler quando pagamento é completado
    const handlePaymentComplete = async (paymentData) => {
      paymentCompleted.value = true;
      paymentProcessing.value = false;
      registrationStatus.value = 'completed';

      // Atualiza status no backend
      try {
        const response = await api.post(`/participants/${participant.value.id}/complete-payment`, {
          payment_data: paymentData
        });

        if (response.data) {
          participant.value = response.data;

          // Registra conversão no tracking
          if (response.data.id) {
            trackConversion(response.data.id).catch(err =>
              console.warn('[SourceTracker] Erro ao registrar conversão:', err)
            );
          }

          // Captura thank you page data
          if (response.data.thank_you_page) {
            thankYouPageData.value = response.data.thank_you_page;
          }

          // Captura confirmation code
          if (response.data.confirmation_code) {
            confirmationCode.value = response.data.confirmation_code;
          }

          // Mostra tela de sucesso
          showForm.value = false;
          showThanks.value = true;
          scrollToFormRoot();
        }
      } catch (error) {
        console.error('❌ Error completing payment:', error);
        formError.value = 'Erro ao finalizar pagamento. Tente novamente.';
      }
    };

    // Registra participante via /form/submit com validação completa (quando hasPayment é true)
    const registerParticipant = async () => {
      if (isSubmitting.value || loading.value) return; // Guard clause
      loading.value = true;
      isSubmitting.value = true;

      try {
        const formDataToSend = new FormData();

        // Campos do formulário (pula File — tratados no loop de files.value abaixo)
        Object.keys(formData).forEach(key => {
          let value = formData[key];
          if (value instanceof File) return; // Evita duplicação com o loop de files
          // Converter DD/MM/YYYY para YYYY-MM-DD (formato esperado pela API)
          if (typeof value === 'string' && /^\d{2}\/\d{2}\/\d{4}$/.test(value)) {
            const [dd, mm, yyyy] = value.split('/');
            value = `${yyyy}-${mm}-${dd}`;
          }
          // Mapear nome de volta ao original (campos com name duplicado no schema)
          // Suporta companion_N_ prefix: strip prefix → lookup → re-add prefix
          let originalKey;
          const companionMatch = key.match(/^(companion_\d+_)(.+)$/);
          if (companionMatch) {
            originalKey = companionMatch[1] + (fieldNameMap[companionMatch[2]] || companionMatch[2]);
          } else {
            originalKey = fieldNameMap[key] || key;
          }
          if (value !== null && value !== undefined && value !== '') {
            formDataToSend.append(originalKey, value);
          }
        });

        // Arquivos
        Object.keys(files.value).forEach(key => {
          const file = files.value[key];
          if (file instanceof File) {
            let originalKey;
            const companionMatch = key.match(/^(companion_\d+_)(.+)$/);
            if (companionMatch) {
              originalKey = companionMatch[1] + (fieldNameMap[companionMatch[2]] || companionMatch[2]);
            } else {
              originalKey = fieldNameMap[key] || key;
            }
            formDataToSend.append(originalKey, file);
          }
        });

        // Selfie do reconhecimento facial
        if (facialBlob.value) {
          formDataToSend.append('selfie', facialBlob.value, 'selfie.jpg');
        }

        // ID do participante (se lista importada)
        if (participant.value?.id) {
          formDataToSend.append('id', participant.value.id);
          formDataToSend.append('participant_id', participant.value.id);
        }

        // LGPD
        formDataToSend.append('authorizes_data_usage', 1);

        // Registration Control
        if (registrationControlItemId.value) {
          formDataToSend.append('registration_control_item_id', registrationControlItemId.value);
        }

        // Source Tracking (UTM + referrer)
        const sourceData = getSourceDataForApi();
        if (sourceData) {
          formDataToSend.append('source_tracking', JSON.stringify(sourceData));
        }

        // Flag de checkout: adia journey triggers para após confirmação de pagamento
        formDataToSend.append('is_checkout', '1');

        // Usa submitFormGlobal (com validação completa do formulário dinâmico)
        const response = await submitFormGlobal(
          formId.value,
          formDataToSend,
          participant.value?.id,
          groupId.value
        );

        const responseData = response.data || response;

        if (response.ok !== false && responseData.id) {
          participant.value = responseData;
          registrationStatus.value = 'registered';

          // Registra conversão no tracking (inscrição completada)
          if (responseData.id) {
            console.log('[RSVPForm] Chamando trackConversion com ID:', responseData.id);
            trackConversion(responseData.id).catch(err =>
              console.warn('[SourceTracker] Erro ao registrar conversão:', err)
            );
          }

          // IMPORTANTE: Garantir que eventSlug está definido ANTES de avançar
          if (!eventSlug.value) {
            if (settings.value) {
              const detectedSlug = settings.value.slug || settings.value.event_slug || null;
              if (detectedSlug) {
                eventSlug.value = detectedSlug;
              }
            }

            // Se ainda estiver vazio, usar fallback baseado no participant ou URL
            if (!eventSlug.value) {
              const urlSlug = window.location.pathname.split('/').filter(p => p)[0];
              const fallbackSlug = urlSlug || 'evento';
              eventSlug.value = fallbackSlug;
            }
          }

          // Aguarda próximo tick para garantir que reactive values foram atualizados
          await nextTick();

          // Avança para ticket selection
          wizardStep.value = ticketStepIndex.value;
          formSubmitted.value = false;

          // Scroll to top
          scrollToFormTop();
        } else {
          formError.value = response.message || 'Erro ao registrar participante';
          scrollToFormRoot();
        }
      } catch (error) {
        console.error('Erro ao registrar participante:', error);

        if (error.response?.status === 422) {
          const errors = error.response?.data?.errors;
          if (errors) {
            const allErrors = Object.values(errors)
              .map(msgs => (Array.isArray(msgs) ? msgs[0] : msgs))
              .filter(Boolean);
            formError.value = allErrors.length > 0
              ? allErrors.join('. ')
              : (error.response?.data?.message || 'Dados inválidos. Verifique os campos.');
          } else {
            formError.value = error.response?.data?.message || 'Dados inválidos.';
          }
        } else if (error.response?.status === 409) {
          formError.value = error.response?.data?.message || 'Já existe um cadastro com esses dados.';
        } else {
          formError.value = error.response?.data?.message || 'Erro ao registrar. Tente novamente.';
        }
        scrollToFormRoot();
      } finally {
        loading.value = false;
        isSubmitting.value = false;
      }
    };

    // Submit final
    const handleSubmit = async () => {
      // Guard clause: impede múltiplos submits simultâneos
      if (isSubmitting.value || loading.value) return;

      // Se tem pagamento E está em modo wizard, não deve chamar handleSubmit na revisão
      if (hasPayment.value && isWizardMode.value && isReviewStep.value) {
        return registerParticipant();
      }

      // Bloqueia imediatamente antes de qualquer operação async
      isSubmitting.value = true;
      loading.value = true;

      // Se não é modo wizard, valida antes de enviar
      if (!isWizardMode.value) {
        if (!validateCurrentStep()) {
          loading.value = false;
          isSubmitting.value = false;
          // Scroll para o topo para mostrar erros
          scrollToFormRoot();
          return;
        }
      }

      formSubmitted.value = true;

      try {
        const formDataToSend = new FormData();

        // Campos do formulário (pula File — tratados no loop de files.value abaixo)
        Object.keys(formData).forEach(key => {
          let value = formData[key];
          if (value instanceof File) return; // Evita duplicação com o loop de files
          // Converter DD/MM/YYYY para YYYY-MM-DD (formato esperado pela API)
          if (typeof value === 'string' && /^\d{2}\/\d{2}\/\d{4}$/.test(value)) {
            const [dd, mm, yyyy] = value.split('/');
            value = `${yyyy}-${mm}-${dd}`;
          }
          // Mapear nome de volta ao original (campos com name duplicado no schema)
          // Suporta companion_N_ prefix: strip prefix → lookup → re-add prefix
          let originalKey;
          const companionMatch = key.match(/^(companion_\d+_)(.+)$/);
          if (companionMatch) {
            originalKey = companionMatch[1] + (fieldNameMap[companionMatch[2]] || companionMatch[2]);
          } else {
            originalKey = fieldNameMap[key] || key;
          }
          if (value !== null && value !== undefined && value !== '') {
            formDataToSend.append(originalKey, value);
          }
        });

        // Arquivos
        Object.keys(files.value).forEach(key => {
          const file = files.value[key];
          if (file instanceof File) {
            let originalKey;
            const companionMatch = key.match(/^(companion_\d+_)(.+)$/);
            if (companionMatch) {
              originalKey = companionMatch[1] + (fieldNameMap[companionMatch[2]] || companionMatch[2]);
            } else {
              originalKey = fieldNameMap[key] || key;
            }
            formDataToSend.append(originalKey, file);
          }
        });

        // Selfie do reconhecimento facial
        if (facialBlob.value) {
          formDataToSend.append('selfie', facialBlob.value, 'selfie.jpg');
        }

        // ID do participante (se lista importada)
        if (participant.value?.id) {
          formDataToSend.append('id', participant.value.id);
          formDataToSend.append('participant_id', participant.value.id);
        }

        // LGPD
        formDataToSend.append('authorizes_data_usage', 1);

        // Registration Control
        if (registrationControlItemId.value) {
          formDataToSend.append('registration_control_item_id', registrationControlItemId.value);
        }

        // Source Tracking (UTM + referrer)
        const sourceData = getSourceDataForApi();
        if (sourceData) {
          formDataToSend.append('source_tracking', JSON.stringify(sourceData));
        }

        const response = await submitFormGlobal(
          formId.value,
          formDataToSend,
          participant.value?.id,
          groupId.value
        );

        if (response.ok !== false) {
          const responseData = response.data || response;
          participant.value = responseData;

          // 🎯 Registra conversão no tracking (inscrição completada)
          if (responseData.id) {
            console.log('[RSVPForm] Chamando trackConversion com ID:', responseData.id);
            trackConversion(responseData.id).catch(err =>
              console.warn('[SourceTracker] Erro ao registrar conversão:', err)
            );
          } else {
            console.warn('[RSVPForm] responseData.id não encontrado:', responseData);
          }

          // Captura dados da thank you page
          if (responseData.thank_you_page) {
            thankYouPageData.value = responseData.thank_you_page;
          }

          // Captura código de confirmação
          if (responseData.confirmation_code) {
            confirmationCode.value = responseData.confirmation_code;
          }

          formError.value = null; // Limpa erro
          showForm.value = false;
          showThanks.value = true;
          scrollToFormRoot();
        } else {
          formError.value = response.message || 'Erro ao enviar formulário';
          scrollToFormRoot();
        }
      } catch (error) {
        console.error('Erro ao enviar formulário:', error);

        if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
          formError.value = 'O envio demorou demais. Verifique sua conexão e o tamanho dos arquivos.';
        } else if (error.response?.status === 413) {
          formError.value = 'Arquivo muito grande para o servidor. Reduza o tamanho e tente novamente.';
        } else if (error.response?.status === 422) {
          const errors = error.response?.data?.errors;
          if (errors) {
            const allErrors = Object.values(errors)
              .map(msgs => (Array.isArray(msgs) ? msgs[0] : msgs))
              .filter(Boolean);
            formError.value = allErrors.length > 0
              ? allErrors.join('. ')
              : (error.response?.data?.message || 'Dados inválidos. Verifique os campos.');
          } else {
            formError.value = error.response?.data?.message || 'Dados inválidos.';
          }
        } else {
          formError.value = error.response?.data?.message || 'Erro ao enviar formulário. Tente novamente.';
        }

        formSubmitted.value = false;
        scrollToFormRoot();
      } finally {
        loading.value = false;
        isSubmitting.value = false;
      }
    };

    // ==================== Registration Control ====================
    const fillRegistrationControlFields = () => {
      if (!registrationControlItemData.value || !formSchema.value) return;

      const rcData = registrationControlItemData.value;
      const allFields = [];

      // Collect fields from schema
      if (formSchema.value.fields) {
        allFields.push(...formSchema.value.fields);
      }
      if (formSchema.value.steps) {
        formSchema.value.steps.forEach(step => {
          if (step.fields) allFields.push(...step.fields);
        });
      }

      allFields.forEach(field => {
        if (field.config?.source === 'registration_control' && field.config?.rc_field) {
          const value = rcData[field.config.rc_field];
          if (value !== undefined) {
            formData[field.name] = value;
          }
        }
      });
    };

    const handleRegistrationControlValidation = async () => {
      if (!registrationControlIdentifier.value) {
        registrationControlError.value = 'Informe o identificador.';
        return;
      }

      loading.value = true;
      registrationControlError.value = null;

      try {
        const result = await validateRegistrationControl(registrationControlIdentifier.value);

        registrationControlItemId.value = result.data.id;
        registrationControlItemName.value = result.data.name;
        registrationControlValidated.value = true;
        registrationControlRemainingSlots.value = result.data.remaining_slots; // null = ilimitado

        // Store RC item data for auto-filling fields
        registrationControlItemData.value = {
          identifier: result.data.identifier,
          name: result.data.name,
          ...(result.data.data || {})
        };

        // Armazena form_id retornado pelo controle de inscrição
        if (result.data.form_id) {
          registrationControlFormId.value = result.data.form_id;
        }

        // Agora carrega o formulário (foi adiado até a validação)
        if (!formSchema.value) {
          await loadGlobalForm(registrationControlFormId.value);
        }

        // Auto-fill RC fields after form schema is loaded
        fillRegistrationControlFields();

        // Pre-fill email se veio do step de identificação
        if (identifiedEmail.value) {
          formData.email = identifiedEmail.value;
        }
      } catch (err) {
        registrationControlError.value = err.errMsg || 'Erro ao validar identificador.';
      } finally {
        loading.value = false;
      }
    };

    // Auto-declínio: abre o modal de confirmação (com termo de aceite).
    const showDeclineModal = ref(false);
    const declineAccepted = ref(false);

    const handleDecline = () => {
      if (!participant.value || !participant.value.id) return;
      declineAccepted.value = false;
      showDeclineModal.value = true;
    };

    // Efetiva o declínio (status -> Declinado) após o aceite no modal.
    const confirmDecline = async () => {
      if (!declineAccepted.value || isSubmitting.value || loading.value) return;
      if (!participant.value || !participant.value.id) return;
      try {
        isSubmitting.value = true;
        const response = await api.post('/admin/confirmation/update', {
          participantId: participant.value.id,
          action: 'declined'
        });
        const body = (response && response.data) || {};
        const resetToGuest = body.reset_to_guest === true;
        // reset_to_guest: volta a Convidado (99) e libera nova inscrição; senão Declinado (4)
        if (participant.value) participant.value.status = resetToGuest ? 99 : 4;
        canDecline.value = false;
        canEditData.value = false;
        showDeclineModal.value = false;
        // Página de obrigado do declínio (configurada no Settings RSVP), se houver.
        if (body.thank_you_page) {
          thankYouPageData.value = body.thank_you_page;
          showForm.value = false; // esconde o formulário/cards (mesmo padrão do submit)
          showThanks.value = true;
          scrollToFormRoot();
        } else {
          alert(body.message || (resetToGuest
            ? 'Sua inscrição foi removida. Você pode se inscrever novamente quando quiser.'
            : 'Sua participação foi declinada com sucesso.'));
        }
      } catch (err) {
        alert((err && err.response && err.response.data && err.response.data.message) || 'Não foi possível declinar a participação. Tente novamente.');
      } finally {
        isSubmitting.value = false;
      }
    };

    return {
      // Refs
      formRootRef,
      // State
      loading,
      loaderColor,
      currentStep,
      showForm,
      showThanks,
      formSchema,
      formData,
      files,
      participant,
      thankYouPageData,
      confirmationCode,
      facialBlob,

      // Identificação
      identificationValue,
      identificationError,
      identificationSubmitted,
      identificationType,
      identificationText,

      // Formulário
      wizardStep,
      formSubmitted,
      isSubmitting,
      formError,
      visibleSteps,
      allStepsForNavigation,
      displayedSteps,
      allVisibleFields,
      successScreen,
      successImage,
      canGoBack,
      isWizardMode,
      isReviewStep,
      reviewEnabled,
      isLastFormStep,
      totalStepsWithReview,
      facialEnabled,
      facialStepIndex,
      isFacialStep,
      isTicketStep,
      isCheckoutStep,
      ticketStepIndex,
      checkoutStepIndex,

      // Payment
      hasPayment,
      eventSlug,
      selectedTicket,
      ticketQuantity,
      ticketTotal,
      paymentCompleted,
      paymentProcessing,
      paymentConfig,
      pixData,
      boletoData,
      showPaymentPending,
      registrationStatus,

      // Customization
      customization,

      // RSVP
      rsvpSettings,
      isImportedList,

      // Vuelidate
      v$,

      // Validação assíncrona
      asyncErrors,

      // Permissões de edição
      canEditData,
      canDecline,
      declineOnly,
      handleDecline,
      confirmDecline,
      showDeclineModal,
      declineAccepted,
      allowDataChangeBeforeConfirmation,

      // Helpers
      getVisibleFields,
      getFieldOptions,
      isTextField,
      isPhoneField,
      phoneMaskOpts,
      isCpfField,
      isCnpjField,
      isCepField,
      isSelectField,
      isMultiselectField,
      isRadioField,
      isCheckboxField,
      isTermsAcceptanceField,
      isFakeTicketField,
      isDisclaimerField,
      isInstrucoesField,
      isInstrucoesAcceptanceField,
      isFileField,
      isTextareaField,
      isDateField,
      isDatetimeField,
      getInputType,
      getFieldMaxLength,
      isFieldReadonly,
      isFieldDisabled,
      getFieldContainerClass,
      hasFieldError,
      getFieldErrorMessage,
      getAbbreviatedLabel,
      getFieldDisplayValue,
      hasTermsLink,
      getCheckboxLabel,
      renderCheckboxLabel,
      setFileRef,
      triggerFileInput,
      onFileChange,
      clearFile,
      touchField,
      onFieldInput,

      // Métodos
      handleIdentification,
      handleFacialCapture,
      nextStep,
      prevStep,
      handleSubmit,
      registerParticipant,
      handleTicketSelected,
      handleTicketContinue,
      goBackToTicketSelection,
      handlePixGenerated,
      handleBoletoGenerated,
      handlePaymentComplete,

      // Registration Control
      registrationControlConfig,
      registrationControlIdentifier,
      registrationControlItemId,
      registrationControlError,
      registrationControlValidated,
      registrationControlItemName,
      registrationControlFormId,
      handleRegistrationControlValidation
    };
  }
};
</script>

<style scoped>
/* Dividers e seções */
hr {
  border-color: #e5e7eb;
  margin: 1rem 0;
}

/* Botões */
.button-subs {
  background-color: var(--button-color, #556ee6);
}

.button-subs:hover {
  background-color: var(--button-hover-color, #4559c9);
}

/* Steps */
.text-steps {
  color: var(--button-color, #556ee6);
}

.border-steps {
  border-color: var(--button-color, #556ee6);
}

/* Select placeholder */
select.placeholder-style {
  color: #a0aec0 !important;
}

select option:not([disabled]) {
  color: #1a202c;
}

select option[value=""][disabled] {
  color: #a0aec0;
}

select:not(.placeholder-style) {
  color: #1a202c;
}

/* Garantir que o select ocupe 100% da largura */
select.w-full {
  width: 100% !important;
  box-sizing: border-box;
}

/* Upload box */
.upload-box {
  border: 2px dashed #ccc;
  padding: 20px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s;
  border-radius: 0.5rem;
  background: #f8fafc;
}

.upload-box:hover {
  border-color: #3b82f6;
  background: #eff6ff;
}

/* Mobile: prevent iOS auto-zoom on input focus */
@media screen and (max-width: 767px) {
  input,
  select,
  textarea {
    font-size: 16px !important;
  }
}

/* Form */
.form {
  width: 100%;
}

.relative {
  width: 100%;
}

input.w-full,
select.w-full,
textarea.w-full {
  width: 100% !important;
  box-sizing: border-box;
}

/* Inputs desabilitados/readonly */
input:disabled,
input[readonly],
select:disabled,
textarea:disabled {
  background-color: #f3f4f6;
  cursor: not-allowed;
  opacity: 0.7;
}

/* Radio/Checkbox custom */
.custom-radio {
  accent-color: #556ee6;
}

/* Text colors */
.text-616161 {
  color: #616161;
}

/* Progress styles */
.progress-circle {
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s;
}

.progress-line {
  height: 2px;
  flex-grow: 1;
  transition: background-color 0.3s;
}

/* Validation */
.border-red-500 {
  border-color: #ef4444 !important;
}

input.border-red-500:focus,
select.border-red-500:focus,
textarea.border-red-500:focus {
  outline: none;
  ring: 2px;
  ring-color: #fecaca;
}

/* Grid responsivo */
.flex-wrap {
  flex-wrap: wrap;
}

/* Classes de largura fracionadas */
.w-1\/12 { width: 8.333333%; }
.w-2\/12 { width: 16.666667%; }
.w-5\/12 { width: 41.666667%; }
.w-7\/12 { width: 58.333333%; }
.w-10\/12 { width: 83.333333%; }
.w-11\/12 { width: 91.666667%; }

/* Garante que em mobile os campos ocupem largura total */
@media (max-width: 768px) {
  .md\:w-1\/12,
  .md\:w-2\/12,
  .md\:w-1\/4,
  .md\:w-1\/3,
  .md\:w-5\/12,
  .md\:w-1\/2,
  .md\:w-7\/12,
  .md\:w-2\/3,
  .md\:w-3\/4,
  .md\:w-10\/12,
  .md\:w-11\/12 {
    width: 100% !important;
  }
}

/* Desktop: aplica as larguras corretas */
@media (min-width: 769px) {
  .md\:w-1\/12 { width: 8.333333%; }
  .md\:w-2\/12 { width: 16.666667%; }
  .md\:w-1\/4 { width: 25%; }
  .md\:w-1\/3 { width: 33.333333%; }
  .md\:w-5\/12 { width: 41.666667%; }
  .md\:w-1\/2 { width: 50%; }
  .md\:w-7\/12 { width: 58.333333%; }
  .md\:w-2\/3 { width: 66.666667%; }
  .md\:w-3\/4 { width: 75%; }
  .md\:w-10\/12 { width: 83.333333%; }
  .md\:w-11\/12 { width: 91.666667%; }
}

/* Remove padding negativo em telas pequenas */
@media (max-width: 640px) {
  .flex.flex-wrap.-mx-2 {
    margin-left: 0;
    margin-right: 0;
  }

  .flex.flex-wrap.-mx-2 > * {
    padding-left: 0 !important;
    padding-right: 0 !important;
  }
}

/* Layout flexível para campos em linha */
.block.md\:flex {
  display: block;
}

@media (min-width: 768px) {
  .block.md\:flex {
    display: flex;
  }
}

/* Steps navigation - evita encavalamento */
#step-nav {
  flex-wrap: nowrap;
  max-width: 100%;
}

#step-nav > div {
  flex-shrink: 1;
  min-width: 0;
}

#step-nav .truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: block;
}

/* ===== Auto-declínio: card + modal ===== */
.decline-card {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  text-align: left;
  padding: 12px 14px;
  border: 1px solid #fecaca;
  background: #fef2f2;
  border-radius: 12px;
  cursor: pointer;
  transition: background .15s, border-color .15s;
}
.decline-card:hover:not(:disabled) { background: #fee2e2; border-color: #fca5a5; }
.decline-card:disabled { opacity: .5; cursor: not-allowed; }
.decline-card-icon {
  flex-shrink: 0; width: 38px; height: 38px; border-radius: 9999px;
  background: #fee2e2; color: #dc2626;
  display: flex; align-items: center; justify-content: center;
}
.decline-card-text { flex: 1; min-width: 0; }
.decline-card-title { display: block; font-size: 14px; font-weight: 600; color: #b91c1c; }
.decline-card-sub { display: block; font-size: 12px; color: #ef4444; margin-top: 1px; }
.decline-card-chevron { color: #f87171; flex-shrink: 0; }

.decline-modal-overlay {
  position: fixed; inset: 0; z-index: 60;
  display: flex; align-items: center; justify-content: center;
  padding: 16px;
}
.decline-modal-backdrop { position: absolute; inset: 0; background: rgba(0,0,0,.5); }
.decline-modal {
  position: relative; background: #fff; border-radius: 16px;
  box-shadow: 0 20px 50px rgba(0,0,0,.25);
  width: 100%; max-width: 460px; overflow: hidden;
  animation: declineModalIn .18s ease-out;
}
@keyframes declineModalIn { from { opacity: 0; transform: translateY(8px) scale(.98); } to { opacity: 1; transform: none; } }
.decline-modal-body { padding: 24px 24px 16px; }
.decline-modal-head { display: flex; align-items: center; gap: 12px; margin-bottom: 12px; }
.decline-modal-badge {
  width: 42px; height: 42px; border-radius: 12px; flex-shrink: 0;
  background: #fee2e2; color: #dc2626;
  display: flex; align-items: center; justify-content: center;
}
.decline-modal-title { font-size: 18px; font-weight: 700; color: #111827; margin: 0; }
.decline-modal-desc { font-size: 14px; color: #4b5563; line-height: 1.6; margin: 0; }
.decline-modal-term {
  display: flex; align-items: flex-start; gap: 10px; margin-top: 16px;
  padding: 12px; background: #f9fafb; border: 1px solid #eef0f2; border-radius: 10px; cursor: pointer;
}
.decline-modal-term input { margin-top: 2px; width: 16px; height: 16px; accent-color: #dc2626; flex-shrink: 0; }
.decline-modal-term span { font-size: 13px; color: #374151; line-height: 1.5; }
.decline-modal-foot {
  padding: 14px 24px; background: #f9fafb;
  display: flex; align-items: center; justify-content: flex-end; gap: 10px;
}
.decline-btn-cancel {
  padding: 9px 16px; font-size: 13px; font-weight: 500; color: #6b7280;
  background: transparent; border: none; cursor: pointer; border-radius: 8px;
}
.decline-btn-cancel:hover:not(:disabled) { background: #f3f4f6; color: #374151; }
.decline-btn-confirm {
  padding: 10px 18px; font-size: 13px; font-weight: 700; letter-spacing: .02em;
  color: #fff; background: #dc2626; border: none; border-radius: 8px; cursor: pointer;
}
.decline-btn-confirm:hover:not(:disabled) { background: #b91c1c; }
.decline-btn-confirm:disabled { opacity: .5; cursor: not-allowed; }
.decline-btn-cancel:disabled { opacity: .5; cursor: not-allowed; }

</style>
